(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    root.RealmCommerce = api;
    const start = () => api.mount(root.document, root, root.RealmTheme || {});
    if (root.document.readyState === 'loading') root.document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';

  function actionLabel(button) {
    return button?.querySelector?.('[data-realm-action-label]') || button;
  }

  function setActionLabel(button, value) {
    const label = actionLabel(button);
    if (label && label.textContent !== value) label.textContent = value;
  }

  class CommerceError extends Error {
    constructor(code, message, details = {}) {
      super(message); this.name = 'CommerceError'; this.code = code; Object.assign(this, details);
    }
  }

  function integer(value, minimum = 1) {
    const n = typeof value === 'number' ? value : typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN;
    if (!Number.isSafeInteger(n) || n < minimum) throw new CommerceError('INVALID_QUANTITY', 'Enter a valid whole quantity.');
    return n;
  }

  function lineProperties(value) {
    if (value === undefined || value === null) return {};
    if (typeof value !== 'object' || Array.isArray(value)) throw new CommerceError('INVALID_PROPERTIES', 'Product details could not be verified.');
    const entries = [];
    for (const [key, entry] of Object.entries(value)) {
      if (!key || entry === undefined || entry === null) continue;
      if (!['string', 'number', 'boolean'].includes(typeof entry) || typeof entry === 'number' && !Number.isFinite(entry)) throw new CommerceError('INVALID_PROPERTIES', 'Product details could not be verified.');
      entries.push([key, String(entry)]);
    }
    return Object.fromEntries(entries);
  }

  function productFormProperties(form, fitProperties = {}) {
    const entries = new Map();
    const controls = Array.from(form?.elements || form?.querySelectorAll?.('input[name],select[name],textarea[name]') || []);
    for (const control of controls) {
      const name = control.name || control.getAttribute?.('name') || '';
      const match = /^properties\[([^\]]+)\]$/.exec(name);
      if (!match || match[1] === '_esid' || control.disabled || control.matches?.(':disabled') || control.form && control.form !== form) continue;
      const type = String(control.type || '').toLowerCase();
      if (['button', 'submit', 'reset', 'image'].includes(type) || ['checkbox', 'radio'].includes(type) && !control.checked) continue;
      if (type === 'file') {
        if (control.files?.length) throw new CommerceError('PROPERTY_UPLOAD_REQUIRED', 'Complete this customization using its upload controls before adding it.');
        continue;
      }
      if (type === 'select-multiple') {
        for (const option of Array.from(control.selectedOptions || [])) if (!option.disabled) entries.set(match[1], option.value);
      } else if (control.value !== undefined) entries.set(match[1], control.value);
    }
    if (typeof fitProperties?._esid === 'string' && fitProperties._esid) entries.set('_esid', fitProperties._esid);
    return lineProperties(Object.fromEntries(entries));
  }

  function localeRoutes(config = {}, origin = 'https://realm.invalid') {
    const base = new URL(config.root || '/', origin);
    if (base.origin !== origin || base.search || base.hash) throw new CommerceError('INVALID_ROUTE', 'Same-origin locale routes required.');
    const prefix = base.pathname.endsWith('/') ? base.pathname : base.pathname + '/';
    const path = (value, fallback) => {
      const url = new URL(value || fallback, origin);
      if (url.origin !== origin || url.search || url.hash) throw new CommerceError('INVALID_ROUTE', 'Same-origin cart routes required.');
      return url.pathname.startsWith(prefix) ? url.pathname : prefix + url.pathname.replace(/^\/+/, '');
    };
    const cart = path(config.cart, 'cart');
    return { root: prefix, cart, cartJs: cart.replace(/\/$/, '') + '.js', add: path(config.add, 'cart/add.js'), change: path(config.change, 'cart/change.js') };
  }

  function validCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) throw new CommerceError('INVALID_CART', 'Cart response could not be verified.');
    let count = 0; const keys = new Set();
    for (const line of cart.items) {
      if (typeof line.key !== 'string' || !line.key || !Number.isSafeInteger(line.quantity) || line.quantity < 1) throw new CommerceError('INVALID_CART', 'Cart line could not be verified.');
      if (keys.has(line.key)) throw new CommerceError('INVALID_CART', 'Cart line identities could not be verified.');
      keys.add(line.key);
      count += line.quantity;
    }
    if (count !== cart.item_count) throw new CommerceError('INVALID_CART', 'Cart quantities could not be verified.');
    return cart;
  }

  function merchandiseItemCount(cart, protection) {
    validCart(cart);
    const productId = String(protection?.product_id || '');
    if (!/^[1-9]\d*$/.test(productId) || protection?.handle !== 'shipping-protection') return cart.item_count;
    return cart.item_count - cart.items.reduce((count, line) => count + (String(line.product_id) === productId ? line.quantity : 0), 0);
  }

  function cartFingerprint(cart) {
    return JSON.stringify(cart.items.map(line => ({ key: line.key, variant: String(line.variant_id || line.id), quantity: line.quantity })).sort((a, b) => a.key.localeCompare(b.key)));
  }

  function stableValue(value) {
    if (Array.isArray(value)) return value.map(stableValue);
    if (value && typeof value === 'object') return Object.fromEntries(Object.keys(value).sort().map(key => [key, stableValue(value[key])]));
    return value === undefined ? null : value;
  }
  function checkoutCartSignature(cart) {
    validCart(cart);
    const fields = ['currency', 'item_count', 'total_price', 'original_total_price', 'total_discount', 'items_subtotal_price', 'taxes_included', 'note', 'attributes', 'cart_level_discount_applications', 'discount_codes'];
    const lineFields = ['key', 'variant_id', 'id', 'quantity', 'properties', 'selling_plan_allocation', 'price', 'original_price', 'discounted_price', 'final_price', 'line_price', 'original_line_price', 'final_line_price', 'total_discount', 'discounts', 'line_level_discount_allocations'];
    return JSON.stringify(stableValue({ ...Object.fromEntries(fields.map(key => [key, cart[key]])), items: cart.items.map(line => Object.fromEntries(lineFields.map(key => [key, line[key]]))).sort((a, b) => a.key.localeCompare(b.key)) }));
  }
  function checkoutProtectionSignature(cart, config) {
    validCart(cart);
    const productId = String(config?.product_id || '');
    if (!/^[1-9]\d*$/.test(productId) || config?.handle !== 'shipping-protection') throw new CommerceError('PROTECTION_GUARD_UNAVAILABLE', 'Shipping protection could not be verified.', { checkoutReason: 'protectionPending' });
    const fees = cart.items.filter(line => String(line.product_id) === productId);
    if (fees.length > 1 || fees.some(line => line.quantity !== 1)) throw new CommerceError('PROTECTION_PENDING', 'Shipping protection could not be verified.', { checkoutReason: 'protectionPending' });
    const fields = ['product_id', 'variant_id', 'id', 'quantity', 'properties', 'selling_plan_allocation', 'price', 'original_price', 'discounted_price', 'final_price', 'line_price', 'original_line_price', 'final_line_price', 'total_discount', 'line_level_total_discount', 'discounts', 'discount_allocations', 'line_level_discount_allocations'];
    return JSON.stringify(stableValue(fees.map(line => Object.fromEntries(fields.map(key => [key, line[key]])))));
  }
  function bundleLineIdentity(line) {
    const id = String(line.variant_id || line.variantId || line.id || '');
    const plan = line.selling_plan_allocation?.selling_plan?.id ?? line.sellingPlan?.selling_plan?.id ?? line.sellingPlan;
    if (!/^\d+$/.test(id) || line.properties && (typeof line.properties !== 'object' || Array.isArray(line.properties)) || plan != null && !/^\d+$/.test(String(plan))) return null;
    return id + '|' + JSON.stringify(stableValue(line.properties || {})) + '|' + (plan == null ? '' : String(plan));
  }
  function currentBundleAttempt(attempt, now = Date.now()) {
    return Number.isSafeInteger(attempt?.started) && attempt.started > 0 && attempt.started <= now + 5000 && now - attempt.started <= 15 * 60 * 1000;
  }
  function reconcileBundleCheckout(attempt, cart) {
    if (!attempt || !Array.isArray(attempt.expected) || !attempt.expected.length || !Array.isArray(attempt.baseline?.items)) return 'uncertain';
    if (!currentBundleAttempt(attempt) || !/^[A-Z]{3}$/.test(attempt.baseline.currency || '') || attempt.baseline.currency !== cart.currency) return 'uncertain';
    const counts = lines => {
      const result = new Map();
      for (const line of lines) {
        const key = bundleLineIdentity(line);
        if (!key || !Number.isSafeInteger(line.quantity) || line.quantity < 1) return null;
        result.set(key, (result.get(key) || 0) + line.quantity);
      }
      return result;
    };
    const before = counts(attempt.baseline.items), after = counts(cart.items), expected = counts(attempt.expected);
    if (!before || !after || !expected || attempt.expected.some(line => !Object.prototype.hasOwnProperty.call(line, 'properties') || !Object.prototype.hasOwnProperty.call(line, 'sellingPlan'))) return 'uncertain';
    let matched = 0;
    for (const key of new Set([...before.keys(), ...after.keys(), ...expected.keys()])) {
      const delta = (after.get(key) || 0) - (before.get(key) || 0), required = expected.get(key) || 0;
      if (delta !== required) return delta > 0 && required > 0 ? 'partial' : 'uncertain';
      if (required) matched++;
    }
    return matched === expected.size ? 'confirmed' : 'uncertain';
  }
  function bundleAttemptSignature(attempt) {
    if (!Number.isSafeInteger(attempt?.started) || attempt.started <= 0 || !Array.isArray(attempt.expected) || !attempt.expected.length || !Array.isArray(attempt.baseline?.items) || typeof attempt.baseline.currency !== 'string') return null;
    const lines = (items, expected = false) => {
      const values = items.map(line => {
        if (!line || typeof line !== 'object') return null;
        const identity = bundleLineIdentity(line);
        if (!identity || !Number.isSafeInteger(line.quantity) || line.quantity < 1 || expected && (!Object.prototype.hasOwnProperty.call(line, 'properties') || !Object.prototype.hasOwnProperty.call(line, 'sellingPlan') || line.quantity !== 1)) return null;
        return [identity, line.quantity];
      });
      return values.some(value => !value) ? null : values.sort((a, b) => a[0].localeCompare(b[0]));
    };
    const baseline = lines(attempt.baseline.items), expected = lines(attempt.expected, true);
    if (!baseline || !expected) return null;
    return JSON.stringify([1, attempt.started, attempt.baseline.currency, attempt.baseline.item_count, attempt.baseline.total_price, baseline, expected]);
  }
  function matchesBundleCompletion(attempt, receipt) {
    const signature = bundleAttemptSignature(attempt);
    if (!signature || receipt?.state !== 'confirmed' || !/^[A-Z]{3}$/.test(receipt.observedCurrency || '') || receipt.observedCurrency !== attempt.baseline.currency || receipt.attemptSignature !== signature || receipt.attemptStarted !== attempt.started || !Number.isSafeInteger(receipt.observedAt) || receipt.observedAt < attempt.started || receipt.observedAt > Date.now() + 5000 || !Array.isArray(receipt.deltas) || !Array.isArray(receipt.matched) || !Array.isArray(receipt.missing) || receipt.missing.length) return false;
    if (receipt.deltas.some(line => !line || !Object.prototype.hasOwnProperty.call(line, 'properties') || !Object.prototype.hasOwnProperty.call(line, 'sellingPlan') || line.identity !== bundleLineIdentity(line) || line.quantity !== 1)) return false;
    const expected = attempt.expected.map(line => [bundleLineIdentity(line), line.quantity]).sort((a, b) => a[0].localeCompare(b[0]));
    const actual = receipt.deltas.map(line => [bundleLineIdentity(line), line.quantity]).sort((a, b) => String(a[0]).localeCompare(String(b[0])));
    return JSON.stringify(actual) === JSON.stringify(expected) && JSON.stringify(receipt.matched.slice().sort()) === JSON.stringify(expected.map(line => line[0]).sort());
  }
  function bundleCompletionReceipt(attempt, cart) {
    const deltas = attempt.expected.map(line => ({ variantId: String(line.variantId || line.variant_id || line.id), quantity: line.quantity, identity: bundleLineIdentity(line), properties: line.properties || {}, sellingPlan: line.sellingPlan == null ? null : { selling_plan: { id: line.sellingPlan?.selling_plan?.id ?? line.sellingPlan } } }));
    return { state: 'confirmed', matched: deltas.map(line => line.identity), missing: [], deltas, itemCount: cart.item_count, totalPrice: cart.total_price, observedCurrency: cart.currency, observedAt: Date.now(), nativeError: null, attemptStarted: attempt.started, attemptSignature: bundleAttemptSignature(attempt) };
  }
  class BundleCheckoutRegistry {
    constructor(storage, changed = () => {}) {
      this.storage = storage; this.changed = changed; this.entries = new Map(); this.key = 'realmBundleCheckout:v1'; this.storageError = false;
      try {
        const saved = storage?.getItem(this.key);
        if (saved) {
          const parsed = JSON.parse(saved);
          if (parsed?.version !== 1 || !Array.isArray(parsed.entries)) throw new Error('Invalid bundle state');
          for (const [id, value] of parsed.entries) if (!this.completionFor(id, value.attempt)) this.entries.set(String(id), { phase: 'uncertain', attempt: value.attempt, unresolved: true, restored: true });
        }
      } catch (_) { this.storageError = true; }
    }
    completionFor(id, attempt) {
      if (!/^\d+$/.test(String(id)) || !attempt) return null;
      try {
        const saved = JSON.parse(this.storage?.getItem('realmBundle:v2:' + id) || 'null');
        return saved?.version === 2 && saved.attempt === null && typeof saved.path === 'string' && /\/products\/[a-z0-9_.-]+\/?$/i.test(saved.path) && matchesBundleCompletion(attempt, saved.reconciliation) ? saved.reconciliation : null;
      } catch (_) { return null; }
    }
    recordCompletion(id, attempt, cart) {
      if (!/^\d+$/.test(String(id))) return true;
      try {
        const signature = bundleAttemptSignature(attempt); if (!signature || reconcileBundleCheckout(attempt, cart) !== 'confirmed') return false;
        const key = 'realmBundle:v2:' + id, raw = this.storage?.getItem(key);
        if (!raw) return true;
        const saved = JSON.parse(raw);
        if (saved?.version !== 2 || typeof saved.path !== 'string' || !/\/products\/[a-z0-9_.-]+\/?$/i.test(saved.path)) return false;
        if (this.completionFor(id, attempt)) return true;
        const legacyCompletion = saved.attempt === null && saved.reconciliation?.state === 'confirmed' && saved.reconciliation.attemptSignature === signature && saved.reconciliation.attemptStarted === attempt.started;
        if (bundleAttemptSignature(saved.attempt) !== signature && !legacyCompletion) return false;
        saved.attempt = null; saved.reconciliation = bundleCompletionReceipt(attempt, cart);
        this.storage.setItem(key, JSON.stringify(saved));
        return true;
      } catch (_) { this.storageError = true; return false; }
    }
    persist() {
      try {
        // Review is local to this rendered cart, never a persisted completion.
        const entries = Array.from(this.entries).filter(([, value]) => value.unresolved || value.attempt).map(([id, value]) => {
          const { reviewedSignature, restored, ...saved } = value;
          return [id, saved];
        });
        if (entries.length) this.storage?.setItem(this.key, JSON.stringify({ version: 1, entries }));
        else this.storage?.removeItem(this.key);
      } catch (_) { this.storageError = true; }
    }
    set(id, value) {
      if (!id || !value) return;
      let prior = this.entries.get(String(id));
      // A live bundle controller owns its own reconciliation; a cart review
      // may only acknowledge expired history restored after a navigation.
      if (prior) { prior.restored = false; delete prior.reviewedSignature; }
      if (prior?.attempt && this.completionFor(id, prior.attempt)) { this.entries.delete(String(id)); prior = null; }
      if (value.attempt && this.completionFor(id, value.attempt)) { this.persist(); this.changed(); return; }
      if (prior?.unresolved && !value.attempt && value.phase !== 'confirmed') return;
      if (value.phase === 'confirmed' && !value.attempt && value.reconciliation?.state === 'confirmed' && Number.isSafeInteger(value.reconciliation.observedAt) && value.reconciliation.observedAt >= Date.now() - 15000 && value.reconciliation.observedAt <= Date.now() + 5000) {
        if (!prior?.attempt || this.lastCart && reconcileBundleCheckout(prior.attempt, this.lastCart) === 'confirmed') this.entries.delete(String(id));
        else this.entries.set(String(id), { ...prior, phase: 'uncertain', busy: false });
      }
      else if (prior?.unresolved && !value.attempt) return;
      else if (value.attempt || value.busy) this.entries.set(String(id), { phase: value.phase, attempt: value.attempt || null, unresolved: !!value.attempt, busy: !!value.busy });
      else this.entries.delete(String(id));
      this.persist(); this.changed();
    }
    reconcile(cart) {
      this.lastCart = cart;
      for (const [id, value] of this.entries) if (value.attempt && (this.completionFor(id, value.attempt) || value.phase !== 'submitting' && reconcileBundleCheckout(value.attempt, cart) === 'confirmed' && this.recordCompletion(id, value.attempt, cart))) this.entries.delete(id);
      this.persist();
    }
    reviewRestored(cart) {
      if (!this.lastCart || checkoutCartSignature(cart) !== checkoutCartSignature(this.lastCart)) return;
      const signature = checkoutCartSignature(cart);
      for (const value of this.entries.values()) if (value.restored && !value.busy && value.phase !== 'submitting' && !currentBundleAttempt(value.attempt)) value.reviewedSignature = signature;
      this.changed();
    }
    readiness() {
      // Browser storage availability is not evidence of a failed cart write.
      // Keep unknown historical attempts intact, but allow an explicit review
      // of the freshly rendered cart instead of an indefinite legacy block.
      const signature = this.lastCart ? checkoutCartSignature(this.lastCart) : null;
      if (Array.from(this.entries.values()).some(value => (value.unresolved || value.attempt) && !(value.restored && signature && value.reviewedSignature === signature))) return { ready: false, reason: 'checkoutReconcile' };
      if (Array.from(this.entries.values()).some(value => value.busy)) return { ready: false, reason: 'checkoutChecking' };
      return { ready: true, reason: null };
    }
  }
  function checkoutReadiness(service, bundles, entering = false) {
    if (entering) return { ready: false, reason: 'checkoutEntering' };
    if (service.pending > 0) return { ready: false, reason: 'checkoutChecking' };
    if (service.recovery) return { ready: false, reason: 'checkoutRecovery' };
    if (!service.renderReady || !service.cart) return { ready: false, reason: 'checkoutUnavailable' };
    if (service.cart.item_count === 0) return { ready: false, reason: 'checkoutEmpty' };
    return bundles?.readiness() || { ready: true, reason: null };
  }

  function sectionReadUrl(sectionsUrl, routes, section) {
    const origin = 'https://realm.invalid';
    if (sectionsUrl != null && typeof sectionsUrl !== 'string') throw new CommerceError('INVALID_ROUTE', 'A same-origin source page is required.');
    const source = new URL(sectionsUrl || routes.root, origin);
    const url = new URL(routes.root, origin);
    if (source.origin !== origin || url.origin !== origin) throw new CommerceError('INVALID_ROUTE', 'A same-origin source page is required.');
    url.search = ''; url.searchParams.set('sections', section);
    const preview = source.searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.pathname + url.search;
  }

  class CartService {
    constructor({ fetch, routes, section = 'realm-cart-drawer', sectionsUrl, onSnapshot = () => {}, onState = () => {}, timeout = 15000, validateOperation = async () => {} }) {
      Object.assign(this, { fetch, routes, section, sectionsUrl, onSnapshot, onState, timeout, validateOperation });
      this.cart = null; this.tail = Promise.resolve(); this.pending = 0; this.recovery = null; this.renderReady = false; this.sequence = 0;
    }
    state(value) { this.onState({ busy: this.pending > 0, recovery: this.recovery, renderReady: this.renderReady, ...value }); }
    serialized(task, operation) {
      this.pending++; this.state({ phase: 'pending', operation });
      const run = this.tail.then(task);
      this.tail = run.catch(() => {});
      return run.finally(() => { this.pending--; this.state({ phase: 'settled' }); });
    }
    async request(url, method = 'GET', payload) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), this.timeout);
      try {
        const response = await this.fetch(url, { method, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) }, ...(payload ? { body: JSON.stringify(payload) } : {}), signal: controller.signal });
        let data;
        try { data = await response.json(); }
        catch (_) { throw new CommerceError('INVALID_RESPONSE', 'The server response could not be verified.', { ambiguous: method === 'POST', status: response.status }); }
        if (!response.ok) throw new CommerceError('SERVER_REJECTED', typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'The cart could not be updated.', { status: response.status, ambiguous: method === 'POST' && response.status >= 500 });
        return data;
      } catch (error) {
        if (error instanceof CommerceError) throw error;
        throw new CommerceError('NETWORK_ERROR', 'Connection interrupted. Check the cart before trying again.', { ambiguous: method === 'POST', cause: error });
      } finally { clearTimeout(timer); }
    }
    async sync(sectionHtml, verifiedCart) {
      const cart = validCart(verifiedCart === undefined ? await this.request(this.routes.cartJs) : verifiedCart);
      this.cart = cart; this.renderReady = false;
      this.onSnapshot(cart, null);
      try {
        let html = sectionHtml;
        if (typeof html !== 'string') {
          const sections = await this.request(sectionReadUrl(this.sectionsUrl, this.routes, this.section));
          html = sections[this.section];
        }
        if (typeof html !== 'string' || !html.trim()) throw new CommerceError('SECTION_UNAVAILABLE', 'The cart is confirmed, but its display could not be loaded.', { refreshOnly: true });
        this.onSnapshot(cart, html);
        this.renderReady = true;
        return cart;
      } catch (error) {
        error.refreshOnly = true;
        throw error;
      }
    }
    refresh() {
      return this.serialized(async () => {
        try {
          const cart = await this.sync();
          if (this.recovery && this.recovery.refreshOnly) this.recovery = null;
          this.state({ phase: 'refreshed' }); return cart;
        } catch (error) {
          if (!this.recovery) this.recovery = { operation: null, error, refreshOnly: true, ambiguous: false };
          this.state({ phase: 'error', error }); throw error;
        }
      });
    }
    verifyCheckout() {
      return this.serialized(async () => {
        if (this.recovery) throw new CommerceError('CHECKOUT_RECOVERY_REQUIRED', 'Resolve the cart update before checkout.', { checkoutReason: 'checkoutRecovery' });
        try {
          const previous = this.renderReady && this.cart ? checkoutCartSignature(this.cart) : null;
          const cart = validCart(await this.request(this.routes.cartJs));
          if (previous !== null && previous === checkoutCartSignature(cart)) {
            this.cart = cart;
            this.onSnapshot(cart, null);
            this.state({ phase: 'refreshed' });
            return cart;
          }
          await this.sync(undefined, cart);
          this.state({ phase: 'refreshed' });
          throw new CommerceError('CHECKOUT_CHANGED', 'The cart changed. Review the updated items and total before checkout.', { checkoutReason: 'checkoutChanged', reviewRequired: true });
        } catch (error) {
          if (error.code !== 'CHECKOUT_CHANGED' && !this.recovery) this.recovery = { operation: null, error, refreshOnly: true, ambiguous: false };
          this.state({ phase: 'error', error });
          throw error;
        }
      });
    }
    normalize(operation) {
      if (operation.kind === 'add') return { ...operation, id: integer(operation.id), quantity: integer(operation.quantity), properties: Object.freeze(lineProperties(operation.properties)) };
      if (operation.kind === 'change' && typeof operation.key === 'string' && operation.key) return { ...operation, quantity: integer(operation.quantity, 0) };
      throw new CommerceError('INVALID_OPERATION', 'A current variant or cart line key is required.');
    }
    mutate(operation, { assertActive } = {}) {
      let op;
      try { op = this.normalize(operation); } catch (error) { return Promise.reject(error); }
      return this.serialized(() => this.perform(op, { assertActive }), op);
    }
    async perform(op, { assertActive } = {}) {
      let before; let posted = false; let postSucceeded = false;
      try {
        before = validCart(await this.request(this.routes.cartJs));
        this.cart = before;
        if (op.kind === 'change' && !before.items.some(line => line.key === op.key)) throw new CommerceError('STALE_LINE', 'This cart line changed. Review the updated cart before trying again.', { reviewRequired: true });
        await this.validateOperation(op);
        const payload = op.kind === 'add' ? { items: [{ id: op.id, quantity: op.quantity, ...(op.properties && Object.keys(op.properties).length ? { properties: op.properties } : {}) }] } : { id: op.key, quantity: op.quantity };
        payload.sections = [this.section]; payload.sections_url = this.sectionsUrl || this.routes.cart;
        // Provider fee operations must still belong to the same shopper intent
        // after the serialized preflight read. A cancelled guard is review-only,
        // so Retry can never replay that stale intent as a cart mutation.
        if (assertActive) try { assertActive(); } catch (error) { error.reviewRequired = true; throw error; }
        posted = true;
        const response = await this.request(op.kind === 'add' ? this.routes.add : this.routes.change, 'POST', payload);
        postSucceeded = true;
        const cart = await this.sync(response.sections && response.sections[this.section]);
        this.recovery = null;
        this.state({ phase: 'success', operation: op, operationId: ++this.sequence });
        return { cart, operation: op, confirmed: true };
      } catch (error) {
        let reconciled = false;
        if (posted || error.code === 'STALE_LINE') {
          try { await this.sync(); reconciled = true; } catch (_) {}
        }
        const changed = Boolean(reconciled && before && cartFingerprint(before) !== cartFingerprint(this.cart));
        this.recovery = { operation: op, before, error, ambiguous: posted && (!reconciled || Boolean(error.ambiguous)), changed, reconciled, postSucceeded, refreshOnly: postSucceeded || Boolean(error.refreshOnly), reviewRequired: Boolean(error.reviewRequired) || (posted && changed) };
        this.state({ phase: 'error', error, operation: op }); throw error;
      }
    }
    retry({ assertActive } = {}) {
      return this.serialized(async () => {
        const recovery = this.recovery;
        if (!recovery) return { cart: this.cart, confirmed: true, noReplay: true };
        try { await this.sync(); }
        catch (error) { this.state({ phase: 'error', error }); throw error; }
        if (assertActive) try { assertActive(); } catch (error) {
          error.reviewRequired = true; recovery.reviewRequired = true;
          this.state({ phase: 'error', error }); throw error;
        }
        if (recovery.refreshOnly || !recovery.operation) {
          this.recovery = null; this.state({ phase: 'refreshed' });
          return { cart: this.cart, confirmed: true, noReplay: true, operation: recovery.postSucceeded ? recovery.operation : null };
        }
        const changed = recovery.before && cartFingerprint(recovery.before) !== cartFingerprint(this.cart);
        if (changed || recovery.reviewRequired) {
          this.recovery = null;
          const error = new CommerceError('REVIEW_REQUIRED', 'The cart changed. Review its current contents before adding or changing anything again.');
          this.state({ phase: 'review', error });
          return { cart: this.cart, confirmed: false, noReplay: true, reviewRequired: true };
        }
        this.recovery = null;
        return this.perform(recovery.operation, { assertActive });
      }, this.recovery && this.recovery.operation);
    }
  }

  class VariantSelection {
    constructor(product, totalCount) {
      this.product = product; this.totalCount = Number(totalCount || product.variants_count || product.variantsCount || (product.variants || []).length);
      this.options = new Array((product.options || []).length).fill(null);
    }
    choose(index, value) {
      if (!Number.isInteger(index) || index < 0 || index >= this.options.length || typeof value !== 'string' || !value) throw new CommerceError('INVALID_OPTION', 'Choose a valid option.');
      this.options[index] = value; return this.resolve();
    }
    resolve() {
      const variants = this.product.variants || [];
      if (this.totalCount > 250 || this.totalCount > variants.length) return { ready: false, reason: 'variantLimit', variant: null };
      const only = variants[0];
      const defaultOnly = variants.length === 1 && (only.title === 'Default Title' || (only.options || [only.option1])[0] === 'Default Title') && (this.product.options || []).every(option => (typeof option === 'string' ? option : option.name) === 'Title');
      if (!defaultOnly && (!this.options.length || this.options.some(value => value === null))) return { ready: false, reason: 'chooseOptions', variant: null };
      const variant = defaultOnly ? only : variants.find(v => this.options.every((value, i) => (v.options || [v.option1, v.option2, v.option3])[i] === value));
      if (!variant) return { ready: false, reason: 'unavailable', variant: null };
      if (variant.available !== true) return { ready: false, reason: variant.available === false ? 'unavailable' : 'availabilityPending', variant };
      return { ready: true, reason: null, variant };
    }
    update(product) {
      const prior = this.product;
      this.product = { ...prior, ...product, variants: (product.variants || []).map(variant => {
        const previous = prior.variants.find(v => String(v.id) === String(variant.id)) || {};
        const merged = { ...previous, ...variant };
        for (const key of ['inventory_management', 'inventory_policy', 'inventory_quantity', 'realm_inventory_rendered_at']) if (!Object.prototype.hasOwnProperty.call(variant, key)) delete merged[key];
        if (Number(variant.price) !== Number(previous.price)) { if (!variant.price_money) delete merged.price_money; if (!variant.price_formatted) delete merged.price_formatted; }
        // Ajax product reads are authoritative. A removed/omitted compare price
        // must not retain a discount or cached text from the initial Liquid JSON.
        if (!Object.prototype.hasOwnProperty.call(variant, 'compare_at_price')) merged.compare_at_price = null;
        if (merged.compare_at_price !== previous.compare_at_price) {
          if (!variant.compare_at_price_money) delete merged.compare_at_price_money;
          if (!variant.compare_at_price_formatted) delete merged.compare_at_price_formatted;
        }
        return merged;
      }) };
      for (const bound of ['min', 'max']) if (product['price_' + bound] !== prior['price_' + bound] && !product['price_' + bound + '_money']) delete this.product['price_' + bound + '_money'];
      this.totalCount = Number(product.variants_count || product.variantsCount || this.totalCount);
      return this.resolve();
    }
  }

  function productSelection(model, preorderPending) {
    const state = model.resolve();
    if (preorderPending) return { ...state, ready: false, reason: 'preorderPending' };
    if (state.variant && (model.product.requires_selling_plan === true || state.variant.requires_selling_plan === true)) return { ...state, ready: false, reason: 'availabilityPending' };
    if (state.ready) { try { quantityBounds(state.variant); } catch (_) { return { ...state, ready: false, reason: 'quantityRulePending' }; } }
    return state;
  }

  function productInventoryState(variant, contract, threshold = 5, { pending = false, failed = false } = {}) {
    if (pending || failed || !contract || !variant) return null;
    if (variant.available !== true) return null;
    if (contract.purchase_mode !== 'standard' || variant.realm_inventory_rendered_at !== contract.rendered_at) return null;
    if (variant.inventory_management === null) return 'in_stock';
    if (typeof variant.inventory_management !== 'string' || !variant.inventory_management.trim() || !Number.isSafeInteger(variant.inventory_quantity)) return null;
    const limit = Number.isSafeInteger(Number(threshold)) && Number(threshold) >= 1 && Number(threshold) <= 20 ? Number(threshold) : 5;
    if (variant.inventory_quantity > 0) return variant.inventory_quantity <= limit ? 'low_stock' : 'in_stock';
    return null;
  }

  function renderProductInventory(node, state) {
    if (!node) return;
    const text = ['in_stock', 'low_stock'].includes(state) && node.dataset[state.replace(/_([a-z])/g, (_, char) => char.toUpperCase())] || '';
    if (node.hidden !== !text) node.hidden = !text;
    if (node.textContent !== text) node.textContent = text;
    node.dataset.inventoryState = text ? state : '';
    const readyLabel = node.closest?.('[data-realm-product]')?.querySelector('[data-product-stock-label]');
    if (readyLabel) readyLabel.hidden = !text;
  }

  function quantityBounds(variant = {}) {
    const rule = variant.quantity_rule || {};
    const min = Number(rule.min == null ? 1 : rule.min), increment = Number(rule.increment == null ? 1 : rule.increment), max = rule.max == null ? null : Number(rule.max);
    if (!Number.isSafeInteger(min) || min < 1 || !Number.isSafeInteger(increment) || increment < 1 || (max !== null && (!Number.isSafeInteger(max) || max < min))) throw new CommerceError('QUANTITY_RULE', 'The quantity rule could not be verified.');
    return { min, increment, max };
  }

  function quantityAllowed(value, variant) {
    const quantity = integer(value); const { min, increment, max } = quantityBounds(variant);
    if (quantity < min || (max !== null && quantity > max) || (quantity - min) % increment !== 0) throw new CommerceError('QUANTITY_RULE', 'Choose a quantity allowed for this item.');
    return quantity;
  }

  function steppedQuantity(value, direction, variant) {
    if (direction !== -1 && direction !== 1) throw new CommerceError('INVALID_QUANTITY', 'Quantity direction must be previous or next.');
    const { min, increment, max } = quantityBounds(variant);
    let current;
    try { current = quantityAllowed(value, variant); } catch (_) { return min; }
    const upper = max === null ? Number.MAX_SAFE_INTEGER : min + Math.floor((max - min) / increment) * increment;
    return Math.max(min, Math.min(upper, current + direction * increment));
  }

  function galleryIndex(current, direction, count) {
    if (!Number.isInteger(count) || count < 1 || !Number.isInteger(current) || (direction !== -1 && direction !== 1)) throw new CommerceError('INVALID_GALLERY', 'Gallery selection could not be verified.');
    return ((current + direction) % count + count) % count;
  }

  function formatVariantPrice(variant, config, locale) {
    if (typeof variant.price_money === 'string') return variant.price_money;
    if (typeof variant.price_formatted === 'string') return variant.price_formatted;
    if (!Number.isSafeInteger(variant.price) || variant.price < 0) return null;
    if (typeof config.formatMoney === 'function') { try { const value = config.formatMoney(variant.price, config.moneyFormat); return typeof value === 'string' ? value : null; } catch (_) { return null; } }
    if (!/^[A-Z]{3}$/.test(config.currency || '')) return null;
    try { const whole = ['GBP', 'USD', 'EUR'].includes(config.currency) && variant.price % 100 === 0; return new Intl.NumberFormat(locale || undefined, { style: 'currency', currency: config.currency, ...(whole ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : {}) }).format(variant.price / 100) + ' ' + config.currency; } catch (_) { return null; }
  }

  function variantPriceState(variant, config = {}, locale) {
    if (!variant || !Number.isSafeInteger(variant.price) || variant.price < 0) return null;
    const current = formatVariantPrice(variant, config, locale);
    if (current === null) return null;
    const sale = Number.isSafeInteger(variant.compare_at_price) && variant.compare_at_price > variant.price;
    const compare = sale ? formatVariantPrice({ price: variant.compare_at_price, price_money: variant.compare_at_price_money, price_formatted: variant.compare_at_price_formatted }, config, locale) : null;
    return { current, compare, onSale: sale && compare !== null };
  }

  function filteredCardVariant(product, totalCount, values) {
    if (!Array.isArray(values) || values.some(value => typeof value !== 'string' || !value)) return null;
    const sizes = [...new Set(values || [])].filter(value => typeof value === 'string' && value);
    const options = product.options || [], variants = product.variants || [], count = Number(totalCount || product.variants_count || product.variantsCount || variants.length);
    if (sizes.length !== 1 || options.length !== 1 || (typeof options[0] === 'string' ? options[0] : options[0]?.name)?.toLowerCase() !== 'size' || !Number.isInteger(count) || count < 1 || count > 250 || count !== variants.length) return null;
    const matches = variants.filter(variant => (variant.options || [variant.option1])[0] === sizes[0]);
    return matches.length === 1 ? matches[0] : null;
  }

  function productPriceState(product, config = {}, locale) {
    const variants = product.variants || [], total = Number(product.variants_count || product.variantsCount || variants.length);
    const complete = total > 0 && total <= 250 && total === variants.length && variants.every(v => Number.isSafeInteger(v.price) && v.price >= 0);
    if (!complete) {
      const min = product.price_min, max = product.price_max;
      if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max) || min < 0 || max < min) return null;
      const low = formatVariantPrice({ price: min, price_money: product.price_min_money }, config, locale), high = formatVariantPrice({ price: max, price_money: product.price_max_money }, config, locale);
      return low === null || high === null ? null : { current: min === max ? low : low + ' – ' + high, compare: null, onSale: false };
    }
    const priceSorted = [...variants].sort((a, b) => a.price - b.price), low = variantPriceState(priceSorted[0], config, locale), high = variantPriceState(priceSorted.at(-1), config, locale);
    if (!low || !high) return null;
    const state = { current: priceSorted[0].price === priceSorted.at(-1).price ? low.current : low.current + ' – ' + high.current, compare: null, onSale: false };
    // Product ranges include unavailable variants, matching Shopify's product
    // prices. A crossed range is shown only when every known variant is on sale.
    if (variants.every(v => Number.isSafeInteger(v.compare_at_price) && v.compare_at_price > v.price)) {
      const comparisons = [...variants].sort((a, b) => a.compare_at_price - b.compare_at_price);
      const compareLow = variantPriceState(comparisons[0], config, locale)?.compare, compareHigh = variantPriceState(comparisons.at(-1), config, locale)?.compare;
      if (compareLow !== null && compareLow !== undefined && compareHigh !== null && compareHigh !== undefined) {
        state.compare = comparisons[0].compare_at_price === comparisons.at(-1).compare_at_price ? compareLow : compareLow + ' – ' + compareHigh; state.onSale = true;
      }
    }
    return state;
  }

  function renderPriceView(node, state, pending = 'Price pending.') {
    const current = node.querySelector?.('[data-price-current]');
    if (!current) { node.textContent = state?.current ?? pending; return; }
    current.textContent = state?.current ?? pending;
    const sale = Boolean(state?.onSale && state.compare !== null);
    const compare = node.querySelector('[data-price-compare]'), compareLabel = node.querySelector('[data-price-compare-label]'), currentLabel = node.querySelector('[data-price-current-label]');
    if (compare) { compare.textContent = sale ? state.compare : ''; compare.hidden = !sale; }
    if (compareLabel) compareLabel.hidden = !sale;
    if (currentLabel) currentLabel.textContent = sale ? node.dataset.priceSaleLabel : node.dataset.priceRegularLabel;
  }

  function defaultTitleProduct(product) {
    const variants = product.variants || [], only = variants[0];
    return variants.length === 1 && (only.title === 'Default Title' || (only.options || [only.option1])[0] === 'Default Title') && (product.options || []).every(option => (typeof option === 'string' ? option : option.name) === 'Title');
  }
  function mainProductPriceState(model, config = {}, locale) {
    const selected = model.resolve();
    const product = model.product, prices = (product.variants || []).map(variant => variant.price);
    const completePrices = prices.length > 0 && prices.length <= 250 && Number(product.variants_count || prices.length) === prices.length && prices.every(price => Number.isSafeInteger(price) && price >= 0);
    const varying = completePrices ? prices.some(price => price !== prices[0]) : Number.isSafeInteger(product.price_min) && Number.isSafeInteger(product.price_max) ? product.price_min !== product.price_max : prices.some(price => price !== prices[0]);
    if (selected.variant) return { prompt: false, price: variantPriceState(selected.variant, config, locale) };
    const price = completePrices ? variantPriceState((product.variants || []).reduce((lowest, variant) => variant.price < lowest.price ? variant : lowest), config, locale) : Number.isSafeInteger(product.price_min) && product.price_min >= 0 ? variantPriceState({ price: product.price_min, price_money: product.price_min_money }, config, locale) : null;
    return { prompt: false, price: price ? { current: (varying ? (config.priceFromLabel || 'From') + ' ' : '') + price.current, compare: null, onSale: false } : null };
  }
  function productVariantParameter(search) {
    try { const parameters = new URLSearchParams(search || ''), values = parameters.getAll('variant'); return values.length === 1 && /^\d+$/.test(values[0]) ? values[0] : values.length ? 'invalid' : null; }
    catch (_) { return 'invalid'; }
  }
  function productOwnedVariant(product, search) {
    const id = productVariantParameter(search), variants = product.variants || [];
    if (!id || id === 'invalid' || Number(product.variants_count || variants.length) > variants.length || variants.length > 250) return null;
    return variants.find(variant => String(variant.id) === id) || null;
  }
  function applyProductOptions(entry, choices) {
    const count = entry.model.options.length;
    if (!Array.isArray(choices) || choices.length !== count || choices.some(value => value !== null && (typeof value !== 'string' || !value))) return false;
    const controls = Array.from(entry.form.querySelectorAll('[data-option-index]'));
    if (choices.some((value, index) => value !== null && !controls.some(control => Number(control.dataset.optionIndex) === index && (control.tagName === 'SELECT' ? Array.from(control.options || []).some(option => option.value === value) : control.value === value)))) return false;
    entry.model.options = choices.slice();
    for (const control of controls) {
      const value = choices[Number(control.dataset.optionIndex)];
      if (control.type === 'radio' || control.type === 'checkbox') control.checked = value !== null && control.value === value;
      else if (control.tagName === 'SELECT') { control.value = value === null ? '' : value; if (value === null) control.selectedIndex = -1; }
      else control.value = value === null ? '' : value;
    }
    return true;
  }
  function readProductIntent(entry, window) {
    try {
      const navigation = window.performance?.getEntriesByType?.('navigation')?.[0]?.type;
      const state = window.history?.state, saved = state?.realmProductIntent, refresh = state?.realmProductContextRefresh;
      const contextReload = navigation === 'reload' && refresh?.productId === String(entry.model.product.id) && refresh.path === window.location.pathname && refresh.variantParameter === productVariantParameter(window.location.search);
      if (refresh) { const next = { ...state }; delete next.realmProductContextRefresh; window.history.replaceState(next, ''); }
      if (navigation !== 'back_forward' && !contextReload) return null;
      if (!saved || saved.productId !== String(entry.model.product.id) || saved.path !== window.location.pathname || saved.variantParameter !== productVariantParameter(window.location.search)) return null;
      return saved;
    } catch (_) { return null; }
  }
  function seedMainProduct(entry, window, remountIntent) {
    const intent = remountIntent && remountIntent.productId === String(entry.model.product.id) && remountIntent.path === window.location.pathname && remountIntent.variantParameter === productVariantParameter(window.location.search) ? remountIntent : readProductIntent(entry, window);
    if (intent && applyProductOptions(entry, intent.options)) {
      const input = entry.form.querySelector('[name="quantity"]');
      if (input && (Number.isSafeInteger(intent.quantity) && intent.quantity >= 0 || typeof intent.quantity === 'string' && intent.quantity.length <= 32 && /^[-+]?(?:\d+(?:\.\d*)?|\.\d+)?(?:e[-+]?\d+)?$/i.test(intent.quantity))) input.value = String(intent.quantity);
      return intent === remountIntent ? 'remount' : 'history';
    }
    const variant = productOwnedVariant(entry.model.product, window.location.search);
    if (variant && applyProductOptions(entry, variant.options || [variant.option1, variant.option2, variant.option3].slice(0, entry.model.options.length))) return 'url';
    const sizeIndices = (entry.model.product.options || []).map((option, index) => String(typeof option === 'string' ? option : option?.name || '').toLowerCase() === 'size' ? index : -1).filter(index => index >= 0);
    if (sizeIndices.length !== 1) return null;
    const sizeIndex = sizeIndices[0], choices = new Array(entry.model.options.length).fill(null);
    if (!Array.from(entry.form.querySelectorAll('[data-option-index]')).some(control => Number(control.dataset.optionIndex) === sizeIndex && control.value === 'M')) return null;
    choices[sizeIndex] = 'M';
    return applyProductOptions(entry, choices) ? 'default-m' : null;
  }
  function saveProductIntent(entry, window) {
    if (!entry.owner.querySelector('[data-pdp-price]')) return false;
    try {
      const state = window.history?.state, prior = state && typeof state === 'object' && !Array.isArray(state) ? state : {};
      const quantity = String(entry.form.querySelector('[name="quantity"]')?.value ?? '1');
      if (quantity.length > 32 || !/^[-+]?(?:\d+(?:\.\d*)?|\.\d+)?(?:e[-+]?\d+)?$/i.test(quantity)) return false;
      window.history.replaceState({ ...prior, realmProductIntent: { productId: String(entry.model.product.id), path: window.location.pathname, variantParameter: productVariantParameter(window.location.search), options: entry.model.options.slice(), quantity } }, '');
      return true;
    } catch (_) { return false; }
  }
  function focusMissingProductOption(entry, window) {
    const index = entry.model.options.findIndex(value => value === null);
    const control = Array.from(entry.form.querySelectorAll('[data-option-index]')).find(node => Number(node.dataset.optionIndex) === index && !node.disabled);
    if (!control || index < 0) return false;
    control.focus({ preventScroll: true });
    const fieldset = control.closest('fieldset') || control, bounds = fieldset.getBoundingClientRect();
    const headerBounds = entry.owner.ownerDocument?.querySelector('[data-realm-header]')?.getBoundingClientRect();
    const computed = window.getComputedStyle?.(entry.owner.ownerDocument?.documentElement)?.getPropertyValue('--realm-header-height');
    const inset = Math.max(Number(headerBounds?.bottom) || 0, parseFloat(computed) || 0) + 12;
    if (bounds.top < inset || bounds.bottom > window.innerHeight) window.scrollTo({ top: Math.max(0, window.scrollY + bounds.top - inset), left: window.scrollX || 0, behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    return true;
  }

  class DialogController {
    constructor(document, window) {
      this.document = document; this.window = window; this.active = null; this.context = null; this.suspendedGuide = null; this.handoffGeneration = 0; this.swapping = new WeakSet(); this.registered = new WeakSet(); this.animations = new WeakMap();
      this.motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.motionPreference?.addEventListener?.('change', () => {
        if (!this.motionPreference.matches || !this.active) return;
        const dialog = this.active, closing = dialog.dataset.realmDialogState === 'closing';
        this.cancelMotion(dialog); if (closing) this.closeImmediate(dialog, true); else dialog.dataset.realmDialogState = 'open';
      });
      window.addEventListener?.('resize', () => {
        const dialog = this.active;
        if (!dialog) {
          if (this.suspendedGuide && this.context) this.document.body.style.setProperty('width', Math.max(0, this.window.innerWidth - this.context.viewportInset) + 'px');
          return;
        }
        // Keep the locked caller responsive without changing its saved scroll
        // or the width/style priorities restored when the overlay dismisses.
        if (this.context) this.document.body.style.setProperty('width', Math.max(0, this.window.innerWidth - this.context.viewportInset) + 'px');
        if (dialog.id === 'RealmSearch') this.syncSearchBounds(dialog);
        // A guide dismissal becomes immediate at the reference desktop breakpoint.
        if (dialog.id === 'RealmSizeGuide' && dialog.dataset.realmDialogState === 'closing' && this.motion(dialog)?.closeDuration === 0) { this.closeImmediate(dialog, true); return; }
        // Responsive CSS can retarget a running percentage/viewport path. Keep
        // its native interpolation; replace only the completion record/bound.
        const pending = this.animations.get(dialog);
        if (pending && (dialog.id !== 'RealmSearch' || this.motion(dialog)?.kind !== pending.kind)) this.animate(dialog, pending.opening, pending.done);
      });
      document.addEventListener?.('click', event => {
        if (this.suspendedGuide) {
          const next = event.target.closest?.('button, a[href], [data-menu-start], [data-dialog-open]');
          if (next?.matches?.('[data-menu-start],[data-dialog-open="RealmMenu"]')) this.closeImmediate(null, true);
          else if (next?.tagName === 'A' && !next.hasAttribute?.('download') && (!next.target || next.target === '_self')) {
            try { const url = new URL(next.href, this.window.location.origin); if (url.origin !== this.window.location.origin || url.pathname !== this.window.location.pathname || url.search !== this.window.location.search) this.abandon(); } catch (_) {}
          }
        }
        const dialog = this.active;
        if (!dialog || dialog.dataset.realmDialogTail !== 'true' || dialog.contains(event.target)) return;
        const target = event.target.closest?.('button, a[href], input, select, textarea, [data-dialog-open], [data-cart-open], [data-menu-start]');
        if (!target) return;
        // Modal-to-modal intent retains the original caller context; the normal
        // delegated opener upgrades/swaps the tail. A custom menu must start
        // after the old body lock has been restored, before its capture handler.
        if (target.matches?.('[data-cart-open]') || target.dataset?.dialogOpen && target.dataset.dialogOpen !== 'RealmMenu') return;
        let leavesPage = false;
        if (target.tagName === 'A') {
          try { const url = new URL(target.href, this.window.location.origin); leavesPage = url.origin !== this.window.location.origin || url.pathname !== this.window.location.pathname || url.search !== this.window.location.search; } catch (_) {}
        }
        this.closeImmediate(dialog, !leavesPage);
        if (!leavesPage && target.isConnected !== false) target.focus?.({ preventScroll: true });
      }, true);
      this.register(document);
    }
    register(scope = this.document) {
      const dialogs = Array.from(scope.querySelectorAll('dialog.realm-drawer, #RealmSizeGuide, #RealmSearch, #RealmCartDrawer'));
      if (scope.matches?.('dialog.realm-drawer, #RealmSizeGuide, #RealmSearch, #RealmCartDrawer')) dialogs.unshift(scope);
      for (const dialog of dialogs) {
        if (typeof dialog.showModal !== 'function' || dialog.id === 'RealmMenu' || this.registered.has(dialog)) continue;
        this.registered.add(dialog); dialog.dataset ||= {};
        dialog.dataset.realmDialogMotion = this.motion(dialog)?.kind || 'none';
        dialog.dataset.realmDialogState = 'closed';
        dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(dialog); });
        dialog.addEventListener('close', () => { const ours = this.swapping.delete(dialog); if (!ours && !dialog.open && this.active === dialog) { this.cancelMotion(dialog); dialog.dataset.realmDialogState = 'closed'; delete dialog.dataset.realmDialogTail; this.finish(true); } });
        dialog.addEventListener('keydown', event => {
          if (event.key === 'Escape') {
            if (event.target.closest?.('[data-realm-card-product]')?.querySelector('[data-realm-card-sizes][data-open="true"]')) return;
            event.preventDefault(); this.close(dialog); return;
          }
          if (event.key !== 'Tab') return;
          const list = Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')).filter(el => !el.hidden && !el.closest?.('[inert]') && el.getClientRects().length);
          if (!list.length) { event.preventDefault(); dialog.focus(); return; }
          const first = list[0], last = list[list.length - 1];
          if (event.shiftKey && (this.document.activeElement === first || !dialog.contains(this.document.activeElement))) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && (this.document.activeElement === last || !dialog.contains(this.document.activeElement))) { event.preventDefault(); first.focus(); }
        });
      }
    }
    motion(dialog) {
      const desktop = this.window.matchMedia?.('(min-width: 1025px)').matches ?? this.window.innerWidth >= 1025;
      if (dialog.id === 'RealmSearch') return desktop
        ? { kind: 'search-desktop', duration: 280, closeDuration: 240, easing: 'ease', hidden: { clipPath: 'inset(0 0 calc(100% - var(--realm-search-menu-height, 42px)) 0)', opacity: .9 }, visible: { clipPath: 'inset(0)', opacity: 1 }, closed: { clipPath: 'inset(0)', opacity: 0 } }
        : { kind: 'search-mobile', duration: 240, easing: 'ease', hidden: { transform: 'translateY(-8px)', opacity: 0 }, visible: { transform: 'translateY(0)', opacity: 1 } };
      if (dialog.classList?.contains('realm-collection-filter')) return { kind: 'slide', axis: 'x', offset: this.window.innerWidth / (dialog.getBoundingClientRect().width || this.window.innerWidth), duration: 300, easing: 'ease-in-out', hidden: { transform: 'translateX(100vw)', opacity: 1 }, visible: { transform: 'translateX(0)', opacity: 1 } };
      if (dialog.id === 'RealmCartDrawer' || dialog.id === 'RealmLocalization') {
        const mobile = this.window.matchMedia?.('(max-width: 767px)').matches ?? this.window.innerWidth <= 767;
        if (dialog.id === 'RealmCartDrawer' && mobile && dialog.querySelector?.('.realm-cart-lines .realm-cart-line')) return { kind: 'cart-mobile-popup', duration: 220, closeDuration: 180, easing: 'ease', hidden: { transform: 'translateY(18px) scale(.98)', opacity: 0 }, visible: { transform: 'translateY(0) scale(1)', opacity: 1 } };
        return { kind: 'slide', axis: 'x', offset: mobile ? 1.1 : 450 / (dialog.getBoundingClientRect().width || 412), duration: 300, easing: 'ease', hidden: { transform: mobile ? 'translateX(110%)' : 'translateX(450px)', opacity: 1 }, visible: { transform: 'translateX(0)', opacity: 1 } };
      }
      if (dialog.id === 'RealmSizeGuide') return { kind: 'slide', axis: desktop ? 'x' : 'y', offset: desktop ? 795 / (dialog.getBoundingClientRect().width || 792) : 1.1, duration: 300, closeDuration: desktop ? 0 : 300, easing: 'ease', hidden: { transform: desktop ? 'translateX(795px)' : 'translateY(110%)', opacity: 1 }, visible: { transform: desktop ? 'translateX(0)' : 'translateY(0)', opacity: 1 } };
      return null;
    }
    cancelMotion(dialog) {
      const pending = this.animations.get(dialog); this.animations.delete(dialog);
      pending?.cleanup?.(); pending?.animation?.cancel();
    }
    syncSearchBounds(dialog) {
      if (dialog?.id !== 'RealmSearch') return;
      const actions = this.document.querySelector?.('.realm-header-utilities');
      if (!actions || !dialog.style) return;
      const rect = actions.getBoundingClientRect();
      const desktop = this.window.matchMedia?.('(min-width: 1025px)').matches ?? this.window.innerWidth >= 1025;
      // Reference desktop Search follows the outer 32px header inset. Its
      // innerWidth-based right anchor resolves against the scrollbar-excluding
      // viewport; retain both insets when our body lock expands the top layer.
      const viewportInset = this.context?.viewportInset ?? Math.max(0, this.window.innerWidth - this.document.body.getBoundingClientRect().width);
      const panelRight = desktop ? this.window.innerWidth - 32 - viewportInset * 2 : rect.right;
      const panelLeft = desktop ? Math.min(rect.left, panelRight - 347) : rect.left;
      dialog.style.setProperty('--realm-search-left', Math.max(0, panelLeft) + 'px');
      dialog.style.setProperty('--realm-search-right', Math.max(0, this.window.innerWidth - panelRight) + 'px');
      dialog.style.setProperty('--realm-search-top', (desktop ? rect.top + Math.max(0, rect.height - 42) / 2 : rect.top) + 'px');
      dialog.style.setProperty('--realm-search-menu-height', (desktop ? 42 : rect.height) + 'px');
    }
    animate(dialog, opening, done = () => {}) {
      const spec = this.motion(dialog);
      dialog.dataset.realmDialogMotion = spec?.kind || 'none';
      if (spec?.kind === 'search-desktop') { this.animateDesktopSearch(dialog, opening, done); return; }
      // Flush the initial hidden state only when entering the native top layer.
      // Subsequent state changes leave the interpolated value and reversal
      // shortening to CSS rather than restarting an ease from a sampled frame.
      if (spec && opening && dialog.dataset.realmDialogState === 'closed') dialog.getBoundingClientRect();
      this.cancelMotion(dialog); dialog.dataset.realmDialogState = opening ? 'open' : 'closing';
      const duration = opening ? spec?.duration : spec?.closeDuration ?? spec?.duration;
      if (!spec || !duration || this.motionPreference?.matches) { done(); return; }
      const schedule = this.window.setTimeout?.bind(this.window) || setTimeout;
      const unschedule = this.window.clearTimeout?.bind(this.window) || clearTimeout;
      const property = spec.kind === 'search-mobile' || spec.kind === 'cart-mobile-popup' ? 'opacity' : 'transform';
      const record = { opening, kind: spec.kind, done, cleanup: () => { unschedule(record.timer); dialog.removeEventListener?.('transitionend', onEnd); } };
      const finish = () => {
        if (this.animations.get(dialog) !== record || this.active !== dialog || dialog.dataset.realmDialogState !== (opening ? 'open' : 'closing')) return;
        this.animations.delete(dialog); record.cleanup(); done();
      };
      const onEnd = event => {
        if (event.target !== dialog || event.propertyName !== property || !this.nativeTransitionSettled(dialog, opening)) return;
        finish();
      };
      this.animations.set(dialog, record);
      dialog.addEventListener('transitionend', onEnd);
      // This is a missing-event cleanup bound, not the visual transition clock.
      // A shortened native reversal completes at its own transitionend first.
      record.timer = schedule(finish, duration + 50);
    }
    nativeTransitionSettled(dialog, opening) {
      const style = this.window.getComputedStyle?.(dialog), spec = this.motion(dialog);
      if (!style || !spec) return true;
      if (spec.kind === 'search-mobile' || spec.kind === 'cart-mobile-popup') return Math.abs(Number(style.opacity) - (opening ? 1 : 0)) < .001;
      const match = String(style.transform).match(/^matrix(3d)?\(([^)]+)\)$/);
      if (!match) return opening && style.transform === 'none';
      const translation = Number(match[2].split(',')[match[1] ? spec.axis === 'y' ? 13 : 12 : spec.axis === 'y' ? 5 : 4]);
      const rect = dialog.getBoundingClientRect();
      const target = opening ? 0 : (spec.axis === 'y' ? rect.height : rect.width) * spec.offset;
      return Number.isFinite(translation) && Math.abs(translation - target) < .5;
    }
    animateDesktopSearch(dialog, opening, done) {
      this.cancelMotion(dialog);
      // Flush the closed/reversing base state before adding the native opening
      // keyframe. Removing that state lets CSS reverse its underlying opacity
      // transition without forcing a sampled value or an opaque close origin.
      if (opening) dialog.getBoundingClientRect();
      dialog.dataset.realmDialogState = opening ? 'open' : 'closing';
      if (this.motionPreference?.matches) { done(); return; }
      const schedule = this.window.setTimeout?.bind(this.window) || setTimeout;
      const unschedule = this.window.clearTimeout?.bind(this.window) || clearTimeout;
      const eventName = opening ? 'animationend' : 'transitionend';
      const record = { opening, kind: 'search-desktop', done, cleanup: () => { unschedule(record.timer); dialog.removeEventListener?.(eventName, onEnd); } };
      const finish = () => {
        if (this.animations.get(dialog) !== record || this.active !== dialog || dialog.dataset.realmDialogState !== (opening ? 'open' : 'closing')) return;
        this.animations.delete(dialog); record.cleanup(); done();
      };
      const onEnd = event => {
        if (event.target !== dialog) return;
        if (opening ? event.animationName !== 'realm-search-panel' : event.propertyName !== 'opacity' || Number(this.window.getComputedStyle?.(dialog)?.opacity) !== 0) return;
        finish();
      };
      this.animations.set(dialog, record);
      dialog.addEventListener(eventName, onEnd);
      record.timer = schedule(finish, (opening ? 280 : 240) + 50);
    }
    open(dialog, caller) {
      if (!dialog || dialog.id === 'RealmMenu') return;
      if (typeof this.window.CustomEvent === 'function') this.document.dispatchEvent?.(new this.window.CustomEvent('realm:overlay-opening', { detail: { dialogId: dialog.id } }));
      try { this.window.RealmAppStories?.closeForOverlay?.(); } catch (_) {}
      try { this.window.RealmAppReviews?.closeForOverlay?.(); } catch (_) {}
      this.interruptGuide('overlay');
      this.register(dialog);
      this.syncSearchBounds(dialog);
      if (dialog === this.active) {
        if (dialog.dataset.realmDialogState === 'closing') {
          if (dialog.dataset.realmDialogTail === 'true') {
            this.cancelMotion(dialog); this.swapping.add(dialog); dialog.close(); delete dialog.dataset.realmDialogTail;
            try { dialog.showModal(); } catch (error) { this.finish(true); throw error; }
          }
          this.animate(dialog, true);
        }
        return;
      }
      if (!this.context) {
        let returnCaller = caller || this.document.activeElement;
        if (dialog.id === 'RealmLocalization' && returnCaller?.matches?.('[data-realm-localization-menu-opener]') && returnCaller.closest?.('#RealmMenu')) {
          const menuCaller = this.document.querySelector?.('[data-realm-header] .realm-header-menu');
          if (menuCaller?.isConnected && !menuCaller.disabled && !menuCaller.hidden && !menuCaller.closest?.('[inert]') && menuCaller.getClientRects?.().length) returnCaller = menuCaller;
        }
        const body = this.document.body; const styles = {};
        for (const key of ['position', 'top', 'left', 'right', 'width', 'overflow']) styles[key] = [body.style.getPropertyValue(key), body.style.getPropertyPriority(key)];
        const width = body.getBoundingClientRect().width;
        this.context = { x: this.window.scrollX, y: this.window.scrollY, caller: returnCaller, styles, viewportInset: Math.max(0, this.window.innerWidth - width) };
        body.style.setProperty('position', 'fixed'); body.style.setProperty('top', -this.context.y + 'px'); body.style.setProperty('left', -this.context.x + 'px'); body.style.setProperty('right', 'auto'); body.style.setProperty('width', width + 'px'); body.style.setProperty('overflow', 'hidden');
      }
      if (this.active) { this.cancelMotion(this.active); this.active.dataset.realmDialogState = 'closed'; delete this.active.dataset.realmDialogTail; this.swapping.add(this.active); this.active.close(); }
      this.active = dialog;
      try { dialog.showModal(); this.animate(dialog, true); }
      catch (error) { this.finish(true); throw error; }
    }
    close(dialog = this.active) {
      if (!dialog || dialog !== this.active || dialog.dataset.realmDialogState === 'closing') return;
      const spec = this.motion(dialog), duration = spec?.closeDuration ?? spec?.duration;
      if (duration && !this.motionPreference?.matches && typeof dialog.show === 'function') {
        // Native modality is needed while open. During the reference's visual
        // dismissal, only the remaining panel should obstruct the caller.
        // Downgrade to a supported nonmodal dialog without changing CSS timing.
        this.swapping.add(dialog); dialog.close(); dialog.dataset.realmDialogTail = 'true';
        try { dialog.show(); } catch (_) { this.closeImmediate(dialog, true); return; }
      }
      this.animate(dialog, false, () => { if (this.active === dialog) this.closeImmediate(dialog, true); });
    }
    closeImmediate(dialog = this.active, restore = true) {
      if (this.suspendedGuide && (!dialog || dialog === this.suspendedGuide.dialog)) {
        this.interruptGuide(restore ? 'dismiss' : 'navigation'); this.finish(restore); return;
      }
      if (!dialog || dialog !== this.active) return;
      this.cancelMotion(dialog); dialog.dataset.realmDialogState = 'closed'; delete dialog.dataset.realmDialogTail; this.swapping.add(dialog); dialog.close();
      if (this.active === dialog) this.finish(restore);
    }
    finish(restore) {
      this.interruptGuide(restore ? 'dismiss' : 'navigation');
      const context = this.context; this.active = null; this.context = null;
      if (!context) return;
      for (const [key, [value, priority]] of Object.entries(context.styles)) { if (value) this.document.body.style.setProperty(key, value, priority); else this.document.body.style.removeProperty(key); }
      if (restore) this.restoreCallerScroll(context, true);
    }
    restoreCallerScroll(context, focus = false) {
      const htmlStyle = this.document.documentElement.style;
      const previous = [htmlStyle.getPropertyValue('scroll-behavior'), htmlStyle.getPropertyPriority('scroll-behavior')];
      htmlStyle.setProperty('scroll-behavior', 'auto'); this.window.scrollTo(context.x, context.y);
      if (focus && context.caller && context.caller.isConnected) context.caller.focus({ preventScroll: true });
      this.window.requestAnimationFrame(() => { if (previous[0]) htmlStyle.setProperty('scroll-behavior', ...previous); else htmlStyle.removeProperty('scroll-behavior'); });
    }
    suspendGuide(dialog, caller) {
      if (!dialog || dialog.id !== 'RealmSizeGuide' || dialog !== this.active || !dialog.open || !this.context || this.suspendedGuide || dialog.dataset.realmDialogState === 'closing') return null;
      const body = dialog.querySelector?.('[data-guide-body]');
      const token = Object.freeze({ generation: ++this.handoffGeneration });
      const record = { token, dialog, context: this.context, focus: caller || this.document.activeElement, scrollTop: body?.scrollTop || 0, scrollLeft: body?.scrollLeft || 0 };
      this.cancelMotion(dialog); this.swapping.add(dialog); dialog.dataset.realmDialogState = 'suspended'; delete dialog.dataset.realmDialogTail;
      this.suspendedGuide = record; this.active = null; dialog.close();
      return token;
    }
    resumeGuide(token) {
      const record = this.suspendedGuide;
      if (!record || record.token !== token || token.generation !== this.handoffGeneration || this.context !== record.context || this.active) return false;
      if (record.dialog.isConnected === false) { this.interruptGuide('section-removed'); this.finish(true); return false; }
      this.suspendedGuide = null; this.handoffGeneration += 1;
      const context = this.context, bodyStyle = this.document.body.style;
      bodyStyle.setProperty('position', 'fixed'); bodyStyle.setProperty('top', -context.y + 'px'); bodyStyle.setProperty('left', -context.x + 'px'); bodyStyle.setProperty('right', 'auto'); bodyStyle.setProperty('width', Math.max(0, this.window.innerWidth - context.viewportInset) + 'px'); bodyStyle.setProperty('overflow', 'hidden');
      this.active = record.dialog; record.dialog.dataset.realmDialogState = 'closed';
      try { record.dialog.showModal(); record.dialog.dataset.realmDialogState = 'open'; }
      catch (_) { this.finish(true); return false; }
      const guideBody = record.dialog.querySelector?.('[data-guide-body]');
      if (guideBody) { guideBody.scrollTop = record.scrollTop; guideBody.scrollLeft = record.scrollLeft; }
      if (record.focus?.isConnected !== false && record.dialog.contains?.(record.focus)) record.focus.focus?.({ preventScroll: true });
      else record.dialog.querySelector?.('[data-realm-fit-tab][aria-selected="true"],button:not([disabled])')?.focus?.({ preventScroll: true });
      if (guideBody) { guideBody.scrollTop = record.scrollTop; guideBody.scrollLeft = record.scrollLeft; }
      return true;
    }
    interruptGuide(reason = 'overlay') {
      const record = this.suspendedGuide;
      if (!record) return false;
      this.suspendedGuide = null; this.handoffGeneration += 1; record.dialog.dataset.realmDialogState = 'closed';
      if (typeof this.document.dispatchEvent === 'function' && typeof this.window.CustomEvent === 'function') this.document.dispatchEvent(new this.window.CustomEvent('realm:fit-handoff-interrupted', { detail: { reason } }));
      return true;
    }
    abandon() {
      const context = this.context;
      this.closeImmediate(this.active, false);
      if (context && (this.window.scrollX !== context.x || this.window.scrollY !== context.y)) this.restoreCallerScroll(context);
    }
  }

  class GalleryController {
    constructor(main, window) {
      this.main = main; this.window = window; this.layers = new Set(); this.generation = 0; this.pending = null; this.requested = null;
      this.motionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
      this.onMotionChange = () => { if (this.motionPreference.matches && this.requested?.ready) this.commit(this.requested); };
      this.motionPreference?.addEventListener?.('change', this.onMotionChange);
    }
    destroy() {
      this.generation++; this.requested = null; this.cancel();
      for (const layer of this.layers) layer.remove();
      this.layers.clear(); this.motionPreference?.removeEventListener?.('change', this.onMotionChange);
    }
    cancel() {
      const pending = this.pending; this.pending = null;
      if (!pending) return;
      const opacity = this.window.getComputedStyle?.(pending.layer).opacity;
      if (opacity !== undefined) pending.layer.style.opacity = opacity;
      pending.animation.cancel();
    }
    commit(request) {
      if (request.generation !== this.generation) return;
      if (!request.ready) return;
      this.cancel(); this.applySource(this.main, request); this.main.alt = request.alt;
      const slide = this.main.closest?.('.realm-gallery-slide');
      if (slide && request.width > 0 && request.height > 0) slide.style.setProperty('--realm-media-ratio', String(request.width / request.height));
      for (const layer of this.layers) layer.remove();
      this.layers.clear(); this.requested = null;
      const owner = this.main.closest?.('[data-realm-product]');
      if (owner && Number.isInteger(request.index) && request.index >= 0 && request.index < request.count) {
        owner.dataset.galleryActiveIndex = String(request.index);
        for (const button of owner.querySelectorAll('[data-gallery-index]')) button.setAttribute('aria-pressed', String(Number(button.dataset.galleryIndex) === request.index));
        for (const node of owner.querySelectorAll('[data-gallery-count]')) node.textContent = (request.index + 1) + ' // ' + request.count;
      }
    }
    applySource(image, request) {
      image.removeAttribute('srcset'); image.removeAttribute('sizes');
      if (request.width > 0 && request.height > 0) { image.setAttribute('width', String(request.width)); image.setAttribute('height', String(request.height)); }
      const measuredWidth = Number(image.parentElement?.getBoundingClientRect?.().width || this.main.getBoundingClientRect?.().width);
      const sizes = measuredWidth > 0 ? `${Math.ceil(measuredWidth)}px` : request.sizes;
      if (sizes) image.setAttribute('sizes', sizes);
      if (request.srcset) image.setAttribute('srcset', request.srcset);
      image.removeAttribute('data-zoom-src');
      image.src = request.src;
    }
    async show(src, alt, dimensions = {}) {
      const request = { src, alt, width: Number(dimensions.width), height: Number(dimensions.height), srcset: typeof dimensions.srcset === 'string' ? dimensions.srcset : '', sizes: typeof dimensions.sizes === 'string' ? dimensions.sizes : '', index: Number(dimensions.gallery_index), count: Number(dimensions.gallery_count), ready: false, generation: ++this.generation }; this.requested = request;
      this.cancel();
      const layer = this.main.cloneNode(false);
      layer.removeAttribute('data-gallery-main'); layer.removeAttribute('id'); layer.removeAttribute('src');
      layer.classList.add('realm-gallery-layer'); layer.setAttribute('aria-hidden', 'true'); layer.alt = ''; layer.style.opacity = '0'; layer.setAttribute('loading', 'eager'); layer.setAttribute('fetchpriority', 'auto');
      this.applySource(layer, request);
      this.layers.add(layer); this.main.parentElement.append(layer);
      try {
        if (typeof layer.decode === 'function') await layer.decode();
        else if (!layer.complete) await new Promise((resolve, reject) => { layer.addEventListener('load', resolve, { once: true }); layer.addEventListener('error', reject, { once: true }); });
      } catch (_) {
        layer.remove(); this.layers.delete(layer);
        if (request.generation === this.generation) { for (const old of this.layers) old.remove(); this.layers.clear(); this.requested = null; }
        return false;
      }
      if (request.generation !== this.generation || this.requested !== request) { layer.remove(); this.layers.delete(layer); return false; }
      request.ready = true;
      if (this.motionPreference?.matches || typeof layer.animate !== 'function') { this.commit(request); return true; }
      const animation = layer.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease', fill: 'both' });
      const pending = { animation, layer, request }; this.pending = pending;
      animation.onfinish = () => { if (this.pending === pending) this.commit(request); };
      return true;
    }
  }

  function unregisterProducts(products, scope, dispose = entry => { entry.refreshGeneration++; entry.refreshController?.abort(); }) {
    for (const [owner, entry] of products) if (owner.isConnected === false || scope === owner || scope?.contains?.(owner)) { dispose(entry); products.delete(owner); }
    return products;
  }
  function registerProducts(products, scope, initialize, dispose) {
    unregisterProducts(products, null, dispose);
    const owners = Array.from(scope.querySelectorAll('[data-realm-product]'));
    if (scope.matches?.('[data-realm-product]')) owners.unshift(scope);
    for (const owner of owners) {
      if (products.has(owner)) continue;
      const entry = initialize(owner); if (entry) products.set(owner, entry);
    }
    return products;
  }

  function serverCartContent(document, html, surface) {
    const parsed = new document.defaultView.DOMParser().parseFromString(html, 'text/html');
    const source = parsed.querySelector('[data-cart-content]');
    if (!source) throw new CommerceError('SECTION_UNAVAILABLE', 'The cart display could not be verified.');
    source.querySelectorAll('script, iframe, object, embed, base, meta, link').forEach(node => node.remove());
    for (const node of [source, ...source.querySelectorAll('*')]) {
      for (const attr of Array.from(node.attributes)) {
        if (/^on/i.test(attr.name) || attr.name === 'srcdoc' || (/^(href|xlink:href|src|action|formaction)$/i.test(attr.name) && /^(javascript|data|vbscript):/i.test(attr.value.replace(/[\u0000-\u0020]/g, '')))) node.removeAttribute(attr.name);
      }
    }
    if (surface === 'page' || surface === 'drawer') {
      if (surface === 'page') source.querySelectorAll('.realm-cart-recommendations, .realm-cart-empty-footer, .realm-cart-shipping, .realm-cart-feedback, .realm-cart-summary-empty').forEach(node => node.remove());
      const identities = new Map();
      for (const node of source.querySelectorAll('[id]')) {
        const id = node.getAttribute('id');
        const match = /^(RealmCheckout(?:Form|Status|Button|Pending))(?:-(?:page|drawer))?$/.exec(id);
        if (match) {
          const normalized = match[1] + '-' + surface;
          identities.set(id, normalized); node.setAttribute('id', normalized);
        } else if (/^pi-realm-cart-(?:page|drawer)-/.test(id)) {
          const normalized = id.replace(/^pi-realm-cart-(?:page|drawer)-/, 'pi-realm-cart-' + surface + '-');
          identities.set(id, normalized); node.setAttribute('id', normalized);
        }
      }
      for (const node of source.querySelectorAll('[aria-describedby], [aria-labelledby], [for], [form]')) {
        for (const attribute of ['aria-describedby', 'aria-labelledby', 'for', 'form']) {
          if (!node.hasAttribute(attribute)) continue;
          const references = node.getAttribute(attribute).split(/\s+/).filter(Boolean);
          node.setAttribute(attribute, Array.from(new Set(references.map(id => identities.get(id) || id))).join(' '));
        }
      }
      for (const form of source.querySelectorAll('[data-realm-checkout-form]')) form.setAttribute('data-realm-checkout-surface', surface);
      if (surface === 'page') for (const control of source.querySelectorAll('.realm-continue[data-dialog-close]')) {
        const link = parsed.createElement('a'); link.className = control.className; link.textContent = control.textContent;
        link.setAttribute('href', source.querySelector('[data-realm-checkout-form]')?.getAttribute('action')?.replace(/cart\/?$/, 'collections/all') || '/collections/all');
        control.replaceWith(link);
      }
    }
    for (const checkout of source.querySelectorAll('.realm-checkout')) {
      checkout.setAttribute('name', 'checkout');
      checkout.setAttribute('type', 'submit');
      checkout.disabled = !!checkout.getAttribute('data-realm-checkout-server-reason');
    }
    return Array.from(source.childNodes, node => document.importNode(node, true));
  }

  const cartControlKey = control => control.dataset.key || control.dataset.cartKey || control.dataset.cartChange;
  function cartControlAction(control) {
    if (control.dataset.cartAction) return control.dataset.cartAction;
    if (control.tagName === 'INPUT' || control.tagName === 'SELECT') return [control.tagName, control.type, control.name].join(':');
    if (control.dataset.quantity === '0') return 'remove';
    return control.getAttribute('aria-label') || control.textContent.trim();
  }
  function cartFocusTarget(container, oldControls, target) {
    const key = cartControlKey(target); const action = cartControlAction(target);
    const available = control => !control.disabled && !control.hidden && !control.closest?.('[inert]') && (!control.getClientRects || control.getClientRects().length > 0);
    const controls = Array.from(container.querySelectorAll('[data-cart-change]')).filter(available);
    const sameLine = controls.filter(control => cartControlKey(control) === key);
    const sameAction = list => list.find(control => cartControlAction(control) === action);
    if (sameLine.length) return sameAction(sameLine) || sameLine[0];
    const index = oldControls.indexOf(target);
    const nearbyKeys = Array.from(new Set([...oldControls.slice(index + 1), ...oldControls.slice(0, index).reverse()].map(cartControlKey)));
    for (const nearbyKey of nearbyKeys) {
      const nearbyLine = controls.filter(control => cartControlKey(control) === nearbyKey);
      if (nearbyLine.length) return sameAction(nearbyLine) || nearbyLine[0];
    }
    const close = container.closest?.('dialog')?.querySelector('[data-dialog-close]');
    if (close && available(close)) return close;
    return Array.from(container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled])')).find(available) || null;
  }

  function alignCardPriceRows(group) {
    if (!group || group.isConnected === false) return 0;
    const cards = Array.from(group.querySelectorAll('.realm-product-card'));
    for (const card of cards) card.style?.removeProperty('--realm-card-price-height');
    const rows = [];
    for (const card of cards) {
      const price = card.querySelector('.realm-card-price'), rect = card.getBoundingClientRect();
      if (!price || !card.style || rect.width <= 0 || rect.height <= 0) continue;
      const height = price.getBoundingClientRect().height;
      if (!Number.isFinite(height) || height <= 0) continue;
      let row = rows.find(item => Math.abs(item.top - rect.top) < 1);
      if (!row) { row = { top: rect.top, height: 0, cards: [] }; rows.push(row); }
      row.height = Math.max(row.height, height); row.cards.push(card);
    }
    for (const row of rows) for (const card of row.cards) card.style.setProperty('--realm-card-price-height', row.height + 'px');
    return rows.length;
  }
  function createCardPriceRows(document, window) {
    const selector = '.realm-product-grid,.realm-home-product-track,.realm-search-products,.realm-search-result-products';
    const groups = new Set(), pending = new Map();
    const schedule = group => {
      if (!group || pending.has(group)) return;
      if (typeof window.requestAnimationFrame !== 'function') { alignCardPriceRows(group); return; }
      pending.set(group, window.requestAnimationFrame(() => { pending.delete(group); alignCardPriceRows(group); }));
    };
    const observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(entries => entries.forEach(entry => schedule(entry.target))) : null;
    const mount = (scope = document) => {
      for (const group of groups) if (group.isConnected === false) { observer?.unobserve(group); groups.delete(group); }
      const found = Array.from(scope.querySelectorAll?.(selector) || []);
      if (scope.matches?.(selector)) found.push(scope);
      const parent = scope.closest?.(selector); if (parent) found.push(parent);
      for (const group of found) { if (!groups.has(group)) { groups.add(group); observer?.observe(group); } schedule(group); }
    };
    const refresh = () => groups.forEach(schedule);
    document.addEventListener('realm:product-price-updated', event => { if (event.detail?.owner) mount(event.detail.owner); });
    document.addEventListener('click', event => { if (event.target.closest?.('[data-realm-grid-density],[data-realm-mobile-grid-density],[data-realm-search-tab]')) refresh(); });
    document.addEventListener('keydown', event => { if (event.target.closest?.('[data-realm-search-tab]') && ['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) refresh(); });
    document.fonts?.ready?.then(refresh);
    window.addEventListener('resize', refresh);
    window.addEventListener('pagehide', () => { for (const frame of pending.values()) window.cancelAnimationFrame?.(frame); pending.clear(); observer?.disconnect(); });
    window.addEventListener('pageshow', () => { for (const group of groups) observer?.observe(group); refresh(); });
    return { mount, refresh };
  }

  function shippingProgress(cartTotal, { minimumKnown = false, currency, country, threshold = 20000, thresholdCurrency, thresholdSource = 'unknown' } = {}) {
    if (minimumKnown !== true || !['GBP', 'USD', 'EUR'].includes(currency) || thresholdCurrency !== currency || threshold !== 20000 || !Number.isSafeInteger(cartTotal) || cartTotal < 0) return null;
    const gbpCountries = 'AC,AD,AE,AF,AG,AI,AL,AM,AO,AR,AU,AW,AX,AZ,BA,BB,BD,BF,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,BS,BT,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CV,CW,CX,CY,CZ,DJ,DK,DM,DO,DZ,EC,EG,EH,ER,ET,FJ,FK,FO,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GS,GT,GW,GY,HK,HN,HT,HU,ID,IL,IM,IN,IO,IQ,IS,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LY,MA,MC,MD,ME,MF,MG,MK,ML,MM,MN,MO,MQ,MR,MS,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PS,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SJ,SL,SM,SN,SO,SR,SS,ST,SV,SX,SZ,TA,TC,TD,TF,TG,TH,TJ,TK,TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,UM,UY,UZ,VA,VC,VE,VG,VN,VU,WF,WS,XK,YE,YT,ZA,ZM,ZW'.split(',');
    const eurCountries = 'FR,AT,BE,BG,HR,DE,EE,FI,GR,IE,IT,LV,LT,LU,MT,NL,PT,SK,SI,ES'.split(',');
    const matchingRule = currency === 'GBP' && thresholdSource === 'native_automatic' && gbpCountries.includes(country) || currency === 'USD' && thresholdSource === 'native_fixed_zone' && country === 'US' || currency === 'EUR' && thresholdSource === 'native_fixed_zone' && eurCountries.includes(country);
    if (!matchingRule) return null;
    return { cartTotal, threshold, remaining: Math.max(0, threshold - cartTotal), percent: Math.max(0, Math.min(100, cartTotal * 100 / threshold)), reached: cartTotal >= threshold };
  }

  function cartRailPosition(rail, rtl = false) {
    const end = Math.max(0, rail.scrollWidth - rail.clientWidth), position = Math.max(0, Math.min(end, rtl ? -rail.scrollLeft : rail.scrollLeft));
    return { position, end, atStart: position <= 1, atEnd: position >= end - 1 };
  }

  function shippingSnapshotMatches(cart, dataset) {
    if (!cart || !Array.isArray(cart.items) || dataset.shippingCurrency !== cart.currency || Number(dataset.shippingCartTotal) !== cart.total_price || Number(dataset.shippingCartCount) !== cart.item_count) return false;
    try {
      const rendered = JSON.parse(dataset.shippingLines), signature = lines => JSON.stringify(lines.map(line => ({ key: line.key, quantity: line.quantity, final_line_price: line.final_line_price })).sort((a, b) => a.key.localeCompare(b.key)));
      return Array.isArray(rendered) && signature(rendered) === signature(cart.items);
    } catch (_) { return false; }
  }

  class CartRailController {
    constructor(owner, window) {
      this.owner = owner; this.window = window; this.rail = owner.querySelector('[data-cart-rail]'); this.listeners = []; this.drag = null; this.suppressClickUntil = 0;
      this.previous = owner.querySelector('[data-cart-rail-step="-1"]'); this.next = owner.querySelector('[data-cart-rail-step="1"]');
      this.rtl = window.getComputedStyle?.(this.rail)?.direction === 'rtl';
      const listen = (target, type, handler, options) => { target?.addEventListener?.(type, handler, options); this.listeners.push(() => target?.removeEventListener?.(type, handler, options)); };
      listen(this.rail, 'scroll', () => this.update(), { passive: true });
      listen(this.previous, 'click', () => this.step(-1)); listen(this.next, 'click', () => this.step(1));
      listen(this.rail, 'keydown', event => {
        if (event.target !== this.rail) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); this.step((event.key === 'ArrowRight' ? 1 : -1) * (this.rtl ? -1 : 1)); }
        else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); this.scrollTo(event.key === 'Home' ? 0 : cartRailPosition(this.rail, this.rtl).end); }
      });
      listen(this.rail, 'focusin', event => {
        const card = event.target.closest?.('a[href]'); if (!card || !this.rail.contains(card)) return;
        const bounds = this.rail.getBoundingClientRect(), rect = card.getBoundingClientRect();
        const delta = rect.left < bounds.left ? rect.left - bounds.left - 8 : rect.right > bounds.right ? rect.right - bounds.right + 8 : 0;
        if (delta) this.rail.scrollBy({ left: delta, behavior: this.behavior() });
      });
      listen(this.rail, 'pointerdown', event => {
        if (event.isPrimary === false || event.button !== 0) return;
        this.drag = { id: event.pointerId, startX: event.clientX, startY: event.clientY, left: this.rail.scrollLeft, touch: event.pointerType === 'touch', moved: false };
      });
      listen(this.rail, 'pointermove', event => {
        const drag = this.drag; if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.startX, dy = event.clientY - drag.startY;
        if (!drag.moved && Math.abs(dx) > 7 && Math.abs(dx) > Math.abs(dy)) {
          drag.moved = true; this.rail.dataset.dragging = 'true';
          if (!drag.touch) try { this.rail.setPointerCapture?.(drag.id); } catch (_) {}
        }
        if (drag.moved && !drag.touch) { event.preventDefault(); this.rail.scrollLeft = drag.left - dx; this.update(); }
      });
      const finish = event => {
        const drag = this.drag; if (!drag || drag.id !== event.pointerId) return;
        if (drag.moved) this.suppressClickUntil = Date.now() + 500;
        this.drag = null; delete this.rail.dataset.dragging;
        if (!drag.touch) try { if (this.rail.hasPointerCapture?.(drag.id)) this.rail.releasePointerCapture?.(drag.id); } catch (_) {}
        this.update();
      };
      listen(this.rail, 'pointerup', finish); listen(this.rail, 'pointercancel', finish); listen(this.rail, 'lostpointercapture', finish);
      listen(this.rail, 'click', event => { if (event.detail !== 0 && Date.now() < this.suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); } }, true);
      listen(this.rail, 'dragstart', event => event.preventDefault());
      this.observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(() => this.update()) : null;
      this.observer?.observe(this.rail); listen(window, 'resize', () => this.update());
      this.update();
    }
    behavior() { return this.window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; }
    scrollTo(position) { this.rail.scrollTo({ left: position * (this.rtl ? -1 : 1), behavior: this.behavior() }); this.update(); }
    step(direction) {
      const { position, end } = cartRailPosition(this.rail, this.rtl), card = this.rail.querySelector('a[href]');
      const width = card?.getBoundingClientRect?.().width || this.rail.clientWidth * .3;
      this.scrollTo(Math.max(0, Math.min(end, position + direction * (width + 8))));
    }
    update() {
      const position = cartRailPosition(this.rail, this.rtl);
      if (this.previous) { this.previous.disabled = position.atStart; this.previous.setAttribute('aria-disabled', String(position.atStart)); }
      if (this.next) { this.next.disabled = position.atEnd; this.next.setAttribute('aria-disabled', String(position.atEnd)); }
    }
    destroy() { this.listeners.forEach(remove => remove()); this.listeners = []; this.observer?.disconnect(); this.drag = null; delete this.rail.dataset.dragging; }
  }

  function createCartDrawerUI(document, window) {
    const rails = new Map(); let cart = null, state = { busy: true, renderReady: false }, lastShippingMessage = null;
    const drawer = document.getElementById?.('RealmCartDrawer');
    const layout = () => {
      if (!drawer) return;
      const height = drawer.getBoundingClientRect?.().height || drawer.clientHeight;
      if (!height) return;
      const measured = selector => drawer.querySelector(selector)?.getBoundingClientRect?.().height || 0;
      const fixed = measured('.realm-drawer-heading') + measured('.realm-cart-shipping') + measured('.realm-cart-summary');
      const fallback = height < 481 || height - fixed < 160;
      drawer.setAttribute('data-cart-scroll-fallback', String(fallback));
    };
    const observer = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(layout) : null;
    const shipping = () => {
      const region = drawer?.querySelector('[data-cart-shipping]'); if (!region) return;
      const message = region.querySelector('[data-cart-shipping-message]'), track = region.querySelector('[data-cart-shipping-track]');
      if (!region.dataset.shippingMessage) region.dataset.shippingMessage = message?.textContent || '';
      const confirmed = Boolean(state.renderReady && !state.busy && !state.recovery && shippingSnapshotMatches(cart, region.dataset));
      const progress = confirmed ? shippingProgress(cart.total_price, { minimumKnown: region.dataset.shippingMinimumKnown === 'true', currency: cart.currency, country: region.dataset.shippingCountry, threshold: Number(region.dataset.shippingThreshold), thresholdCurrency: region.dataset.shippingThresholdCurrency, thresholdSource: region.dataset.shippingThresholdSource }) : null;
      const text = progress ? region.dataset.shippingMessage : '';
      if (message) { message.hidden = !progress; if (message.textContent !== text) message.textContent = text; }
      if (track) {
        track.hidden = !progress;
        if (progress) { track.setAttribute('aria-valuenow', String(progress.percent)); track.setAttribute('aria-valuetext', text); const fill = track.querySelector('span'); if (fill) fill.style.width = progress.percent + '%'; }
      }
      const announcement = region.querySelector('[data-cart-shipping-announcement]');
      if (confirmed && lastShippingMessage !== null && lastShippingMessage !== text && announcement) announcement.textContent = text;
      if (confirmed) lastShippingMessage = text;
    };
    const mount = () => {
      for (const [owner, rail] of rails) if (owner.isConnected === false) { rail.destroy(); rails.delete(owner); }
      for (const owner of drawer?.querySelectorAll?.('[data-cart-recommendations]') || []) if (!rails.has(owner) && owner.querySelector('[data-cart-rail]')) rails.set(owner, new CartRailController(owner, window));
      observer?.disconnect();
      if (drawer) for (const node of [drawer, drawer.querySelector('.realm-drawer-heading'), drawer.querySelector('.realm-cart-shipping'), drawer.querySelector('.realm-cart-summary')]) if (node) observer?.observe(node);
      shipping(); layout();
    };
    const beforeRender = () => { for (const rail of rails.values()) rail.destroy(); rails.clear(); observer?.disconnect(); };
    window.addEventListener?.('resize', layout);
    window.visualViewport?.addEventListener?.('resize', layout);
    document.addEventListener?.('realm:overlay-opening', event => { if (event.detail?.dialogId === 'RealmCartDrawer') (window.requestAnimationFrame || (callback => callback())).call(window, layout); });
    window.addEventListener?.('pagehide', beforeRender);
    window.addEventListener?.('pageshow', mount);
    document.addEventListener?.('shopify:section:load', mount);
    document.addEventListener?.('shopify:section:unload', event => { for (const [owner, rail] of rails) if (owner.isConnected === false || event.target === owner || event.target?.contains?.(owner)) { rail.destroy(); rails.delete(owner); } });
    mount();
    return { beforeRender, snapshot: value => { cart = value; mount(); }, state: value => { state = value; shipping(); layout(); }, mount, layout };
  }

  function mount(document, window, config) {
    if (document.documentElement.dataset.realmCommerceMounted) return;
    document.documentElement.dataset.realmCommerceMounted = 'true';
    const strings = {
      chooseOptions: 'Choose a size.', unavailable: 'Selected size unavailable. Choose another size.', availabilityPending: 'Availability pending.', preorderPending: 'Preorder details pending. Ordering is unavailable.', quantityRulePending: 'Quantity details pending. Ordering is unavailable.', pricePending: 'Price pending.', variantLimit: 'Variant selection requires the complete option picker.', adding: 'Adding item…', updating: 'Updating cart…', added: 'Item added.', quantityUpdated: 'Quantity updated.', removed: 'Item removed.', refreshed: 'Cart updated.', cartError: 'The cart could not be updated. Try again.', displayError: 'Cart display unavailable. Try again or view the cart page.', retry: 'Retry', refreshPage: 'Refresh page', contextChanged: 'Refresh this page to continue shopping.', checkoutReady: 'Ready for checkout.', checkoutChecking: 'Checking your cart…', checkoutRecovery: 'Resolve the cart update using Retry before checkout.', checkoutUnavailable: 'Cart display unavailable. Retry or reload your cart.', checkoutEmpty: 'Add an item before checkout.', checkoutReconcile: 'Review the items and total in your cart, then select Retry to continue.', checkoutChanged: 'Your cart changed. Review the updated items and total, then select Checkout again.', checkoutEntering: 'Entering checkout…', checkoutFailed: 'Checkout could not open. Review your cart and try again.', contentPending: 'Product details pending. Ordering is unavailable.', configurationPending: 'Product details could not be verified. Retry before ordering.', productChanged: 'Product details changed. Review the updated information and add again.', protectionPending: 'Shipping protection could not be updated. Retry before checkout.', ...config.strings
    };
    const dialogs = new DialogController(document, window);
    const cartDrawerUI = createCartDrawerUI(document, window);
    const products = new Map(); const galleries = new WeakMap(); const disabled = new Map(); const cartBaselineDisabled = new WeakMap(); let busy = false; let pendingCartFocus = null;
    let checkoutInFlight = false, nativeHandoff = false, approvedCheckout = null, checkoutIssue = null;
    let checkoutGeneration = 0, checkoutSuspended = false;
    let bundleStorage; try { bundleStorage = window.sessionStorage; } catch (_) { bundleStorage = { getItem() { throw new Error('Storage unavailable'); } }; }
    const bundles = new BundleCheckoutRegistry(bundleStorage, () => updateCheckout());
    const status = (message, product, kind = 'feedback') => {
      const selectors = product ? ['[data-product-status]'] : ['[data-cart-status]'];
      for (const selector of selectors) for (const node of (product || document).querySelectorAll(selector)) { node.textContent = message; node.setAttribute('data-realm-status-kind', kind); }
    };
    const checkoutDiagnostics = (error, phase) => {
      // Diagnostics are finite reason codes only: never copy provider messages,
      // URLs, cart contents or customer information into the page.
      const codes = ['PROTECTION_GUARD_UNAVAILABLE', 'PROTECTION_PENDING', 'CHECKOUT_CHANGED', 'CHECKOUT_NOT_READY', 'CHECKOUT_FORM_UNAVAILABLE', 'CHECKOUT_GUARD_UNAVAILABLE', 'CHECKOUT_LINE_PENDING', 'CHECKOUT_RECOVERY_REQUIRED', 'INVALID_CART', 'INVALID_QUANTITY', 'SECTION_UNAVAILABLE', 'INVALID_RESPONSE', 'SERVER_REJECTED', 'NETWORK_ERROR'];
      const protectionReasons = ['identity-unavailable', 'cart-invalid', 'fee-shape', 'money-invalid', 'discount-invalid', 'totals-invalid', 'choice-invalid', 'choice-mismatch', 'provider-busy', 'intent-pending', 'generation-changed', 'fee-choice-mismatch', 'fee-remove-unconfirmed', 'merchandise-changed', 'widget-invalid', 'widget-conflict', 'widget-missing', 'cart-busy'];
      const protectionStages = ['identity', 'cart', 'intent', 'settle', 'snapshot', 'assert', 'provider-wait', 'reconcile', 'fee-remove', 'widget', 'recover'];
      const attributes = {
        'data-realm-checkout-error-code': error ? codes.includes(error.code) ? error.code : 'CHECKOUT_GUARD_FAILED' : null,
        'data-realm-checkout-guard-phase': phase || null,
        'data-realm-checkout-protection-reason': error && protectionReasons.includes(error.protectionReason) ? error.protectionReason : null,
        'data-realm-checkout-protection-stage': error && protectionStages.includes(error.protectionStage) ? error.protectionStage : null
      };
      for (const node of document.querySelectorAll('[data-realm-checkout-form], [data-cart-status]')) for (const [name, value] of Object.entries(attributes)) {
        if (value === null) node.removeAttribute(name); else node.setAttribute(name, value);
      }
    };
    const updateSelection = entry => {
      let selection = productSelection(entry.model, false);
      const integration = window.RealmAppIntegration;
      if (!integration?.getContract?.(entry.model.product)) entry.refreshError = true;
      const guard = entry.refreshError ? { ready: false, reason: 'configurationPending' } : integration?.orderingGuard?.(entry.model.product, selection.variant) || { ready: false, reason: 'configurationPending' };
      if (selection.ready && !guard.ready) selection = { ...selection, ready: false, reason: guard.reason };
      const retry = entry.owner.querySelector('[data-product-retry]'); if (retry) { retry.hidden = !entry.refreshError || entry.refreshPending; retry.disabled = busy || !!entry.refreshPending; setActionLabel(retry, entry.pageRefreshRequired ? strings.refreshPage : strings.retry); }
      entry.form.setAttribute('aria-busy', String(busy || !!entry.refreshPending));
      const contract = integration?.getContract?.(entry.model.product), notice = entry.owner.querySelector('[data-purchase-mode-notice]');
      if (notice) { notice.textContent = !contract ? '' : contract.purchase_mode === 'made_to_order' ? 'Made to order.' : integration.preorderNotice(entry.model.product); notice.hidden = !notice.textContent; }
      if (contract) { entry.owner.dataset.purchaseMode = contract.purchase_mode; entry.owner.dataset.preorderTermsState = contract.preorder_terms_state; } const id = entry.form.querySelector('input[name="id"]');
      const mainPrice = entry.owner.querySelector('[data-pdp-price]'), mainState = mainPrice ? mainProductPriceState(entry.model, { ...config, priceFromLabel: mainPrice.dataset.priceFromLabel || config.priceFromLabel }, config.locale || document.documentElement.lang) : null;
      if (mainState && selection.ready && !mainState.price) selection = { ...selection, ready: false, reason: 'pricePending' };
      const inventory = entry.owner.querySelector('[data-product-inventory]');
      renderProductInventory(inventory, productInventoryState(selection.variant, contract, inventory?.dataset.lowStockThreshold, { pending: entry.refreshPending, failed: entry.refreshError || !selection.ready && !['unavailable', 'preorderPending'].includes(selection.reason) }));
      if (inventory) scheduleInventoryExpiry(entry, contract);
      id.disabled = busy || !!entry.refreshPending || !selection.ready; id.value = mainState && selection.variant ? String(selection.variant.id) : selection.ready ? String(selection.variant.id) : '';
      entry.owner.dataset.variantReady = String(selection.ready);
      const button = entry.form.querySelector('[data-add-to-cart]');
      button.disabled = busy || !!entry.refreshPending || !selection.ready;
      button.setAttribute('aria-disabled', String(button.disabled));
      // Labels follow the fresh canonical purchase mode; eligibility remains
      // controlled by the availability, variant, quantity and ordering guards.
      if (!button.dataset.realmAddToCartLabel) button.dataset.realmAddToCartLabel = button.dataset.addToCartLabel || actionLabel(button).textContent;
      if (contract) {
        const soldOutLabel = button.dataset.soldOutLabel || 'Sold out';
        const selectedSoldOut = selection.variant?.available === false;
        setActionLabel(button, selectedSoldOut ? soldOutLabel : contract.purchase_mode === 'preorder' ? button.dataset.preorderLabel || 'PRE ORDER' : button.dataset.realmAddToCartLabel);
        // Describe genuine native availability. Never infer stock from labels,
        // purchase mode, a pending guard or an incomplete variant response.
        if (mainPrice && !entry.refreshError && !entry.refreshPending) {
          const variants = entry.model.product.variants || [];
          const complete = entry.model.totalCount <= 250 && entry.model.totalCount <= variants.length;
          for (const control of entry.owner.querySelectorAll('input[data-option-index]')) {
            const index = Number(control.dataset.optionIndex);
            const matches = complete && Number.isInteger(index) && index >= 0 && index < entry.model.options.length ? variants.filter(variant => {
              const options = variant.options || [variant.option1, variant.option2, variant.option3];
              return options[index] === control.value && entry.model.options.every((value, other) => other === index || value === null || options[other] === value);
            }) : [];
            const soldOut = matches.length > 0 && matches.every(variant => variant.available === false);
            control.dataset.optionUnavailable = String(soldOut);
            control.setAttribute('aria-label', soldOut ? `${control.value}, ${soldOutLabel}` : control.value);
            const availabilityLabel = control.closest('label')?.querySelector('[data-option-sold-out]');
            if (availabilityLabel) { availabilityLabel.textContent = soldOutLabel; availabilityLabel.hidden = !soldOut; }
          }
        }
      }
      const filterVariant = !mainState && !selection.variant && entry.owner.matches('[data-realm-card-product]') && entry.owner.closest('[data-realm-collection]') ? filteredCardVariant(entry.model.product, entry.model.totalCount, Array.from(document.querySelectorAll('input[name="filter.v.option.size"]:checked'), input => input.value)) : null;
      const priceState = mainState ? mainState.price : selection.variant || filterVariant ? variantPriceState(selection.variant || filterVariant, config, config.locale || document.documentElement.lang) : productPriceState(entry.model.product, config, config.locale || document.documentElement.lang);
      for (const node of entry.owner.querySelectorAll('[data-product-price]')) { node.hidden = mainState?.prompt === true; renderPriceView(node, priceState, mainState?.prompt ? '' : strings.pricePending); }
      const pricePrompt = entry.owner.querySelector('[data-price-select-option]');
      if (pricePrompt && mainState) {
        pricePrompt.hidden = !mainState.prompt; pricePrompt.disabled = busy;
        if (mainState.prompt) pricePrompt.textContent = mainState.optionName.toLowerCase() === 'size' ? pricePrompt.dataset.sizePrompt : pricePrompt.dataset.optionPrompt.split('__OPTION__').join(mainState.optionName);
      }
      document.dispatchEvent(new window.CustomEvent('realm:product-price-updated', { detail: { owner: entry.owner, product: entry.model.product } }));
      for (const node of entry.owner.querySelectorAll('[data-selected-size]')) node.textContent = node.dataset.optionLegendIndex !== undefined ? entry.model.options[Number(node.dataset.optionLegendIndex)] || '—' : entry.model.options.filter(value => value !== null).join(' / ');
      updateQuantityControls(entry, selection.variant);
      if (entry.refreshPending) status('Checking product details…', entry.owner);
      else if (entry.pageRefreshRequired) status(strings.contextChanged, entry.owner);
      else if (!selection.ready) status(strings[selection.reason], entry.owner, selection.reason === 'chooseOptions' ? 'instruction' : 'feedback');
      else if (!busy) status('', entry.owner);
      return selection;
    };
    function scheduleInventoryExpiry(entry, contract) {
      const expiry = contract ? (contract.rendered_at + (window.RealmAppIntegration?.maxAgeSeconds || 120) + 1) * 1000 : null;
      if (entry.inventoryExpiry === expiry) return;
      if (entry.inventoryTimer !== undefined) window.clearTimeout(entry.inventoryTimer);
      entry.inventoryExpiry = expiry;
      if (expiry !== null) entry.inventoryTimer = window.setTimeout(() => {
        entry.inventoryExpiry = null;
        if (products.get(entry.owner) === entry && entry.owner.isConnected !== false) renderProductInventory(entry.owner.querySelector('[data-product-inventory]'), null);
      }, Math.max(0, expiry - Date.now()));
    }
    function updateQuantityControls(entry, variant) {
      const input = entry.form.querySelector('[name="quantity"]');
      if (!input) return;
      let rule;
      try { rule = quantityBounds(variant || {}); }
      catch (_) { input.disabled = true; for (const button of entry.owner.querySelectorAll('[data-quantity-step]')) button.disabled = true; return; }
      input.disabled = busy;
      input.min = String(rule.min); input.step = String(rule.increment);
      if (rule.max !== null) input.max = String(rule.max); else input.removeAttribute('max');
      const value = Number(input.value);
      for (const button of entry.owner.querySelectorAll('[data-quantity-step]')) button.disabled = busy || (Number(button.dataset.quantityStep) < 0 ? value <= rule.min : rule.max !== null && value >= rule.max);
    }
    const cardMediaImages = new WeakSet();
    const cardPriceRows = createCardPriceRows(document, window);
    function prepareCardMedia(scope) {
      const cards = Array.from(scope.querySelectorAll('.realm-product-card'));
      if (scope.matches?.('.realm-product-card')) cards.push(scope);
      for (const card of cards) for (const role of ['secondary', 'model']) {
        const image = card.querySelector('.realm-card-' + role);
        const key = role === 'model' ? 'realmModelReady' : 'realmSecondaryReady';
        const available = card.dataset[role === 'model' ? 'realmModelAvailable' : 'realmSecondaryAvailable'] === 'true';
        const update = failed => { card.dataset[key] = String(available && !failed && image?.complete && image.naturalWidth > 0); };
        if (image && !cardMediaImages.has(image)) {
          cardMediaImages.add(image); image.addEventListener('load', () => update(false)); image.addEventListener('error', () => update(true));
        }
        update(false);
      }
    }
    let cardOptionGroup = 0;
    function mountProducts(scope = document) {
      cardPriceRows.mount(scope);
      prepareCardMedia(scope);
      for (const control of disabled.keys()) if (control.isConnected === false) disabled.delete(control);
      const remountIntents = new Map();
      for (const entry of products.values()) if (entry.owner.querySelector('[data-pdp-price]') && entry.intentContext) remountIntents.set(String(entry.model.product.id), { ...entry.intentContext, productId: String(entry.model.product.id), options: entry.model.options.slice(), quantity: String(entry.form.querySelector('[name="quantity"]')?.value ?? '1') });
      return registerProducts(products, scope, owner => {
      try {
        const json = owner.querySelector('script[type="application/json"][data-product-json]'); const form = owner.querySelector('[data-product-form]');
        if (!json || !form || !form.querySelector('input[name="id"]') || !form.querySelector('[data-add-to-cart]')) return null;
        const product = JSON.parse(json.textContent);
        const inlineMissing = !product.realm_commerce_contract; let inlineError;
        try { window.RealmAppIntegration.attachContract(product, product.realm_commerce_contract, config.contractContext); } catch (error) { inlineError = error; }
        const entry = { owner, form, model: new VariantSelection(product, owner.dataset.variantCount), refreshGeneration: 0, refreshError: !window.RealmAppIntegration?.getContract?.(product), refreshPending: false, recoveryAllowed: inlineMissing || !inlineError || inlineError.detail === 'stale' };
        recordProductRefresh(entry, entry.refreshError ? 'error' : 'ready', inlineError);
        const cardGroup = owner.matches('[data-realm-card-product]') ? 'realm-card-options-' + ++cardOptionGroup : null;
        for (const control of form.querySelectorAll('[data-option-index]')) {
          // A product can occur in multiple rails. Keep each picker its own
          // radio group even though these JS controls have no form owner.
          if (cardGroup && control.type === 'radio') control.name = cardGroup + '-' + control.dataset.optionIndex;
          if (control.type === 'radio' || control.type === 'checkbox') control.checked = false;
          else if (control.tagName === 'SELECT') control.selectedIndex = -1;
          else control.value = '';
        }
        if (owner.querySelector('[data-pdp-price]')) { seedMainProduct(entry, window, remountIntents.get(String(product.id))); entry.intentContext = { path: window.location.pathname, variantParameter: productVariantParameter(window.location.search) }; }
        updateSelection(entry);
        if (entry.refreshError && owner.querySelector('[data-pdp-price]') && (inlineMissing || inlineError?.detail === 'stale')) Promise.resolve().then(() => {
          if (products.get(owner) === entry && owner.isConnected !== false) productRuntime.refreshProduct(owner, { onlyIfStale: true }).catch(() => {});
        });
        if (busy) for (const control of owner.querySelectorAll('input, select, [data-realm-card-size-open], [data-add-to-cart]')) { if (!disabled.has(control)) disabled.set(control, control.disabled); control.disabled = true; }
        return entry;
      } catch (_) { status(strings.availabilityPending, owner); return null; }
      }, disposeProduct);
    }
    function disposeProduct(entry) {
      if (entry.owner.querySelector('[data-pdp-price]')) saveProductIntent(entry, window);
      entry.refreshGeneration++; entry.refreshController?.abort();
      if (entry.inventoryTimer !== undefined) window.clearTimeout(entry.inventoryTimer);
      const main = entry.owner.querySelector('[data-gallery-main]');
      if (main) { galleries.get(main)?.destroy(); galleries.delete(main); }
      for (const control of disabled.keys()) if (entry.owner.contains(control)) disabled.delete(control);
    }
    mountProducts();
    function recordProductRefresh(entry, phase, error, transport) {
      entry.owner.dataset.realmProductRefresh = phase;
      entry.pageRefreshRequired = phase === 'error' && error?.detail === 'context';
      const details = ['schema','identity','context','stale','raw-source','title-source','tag-source','ordering-ready-type','mode-type','mode-source','terms-type','terms-mode','unconfirmed-dispatch','batch-owner','batch-verification','batch-source','dispatch-type','dispatch-half-window','dispatch-source','dispatch-window','conflicting-dispatch','primary-contract-missing','primary-contract-multiple','fallback-section-null','fallback-section-missing','fallback-sections-format','fallback-section-format','fallback-contract-missing','fallback-contract-multiple','fallback-response-format','page-contract-missing','page-contract-multiple','page-response-format'];
      entry.owner.dataset.realmProductRefreshReason = error ? error.name === 'TimeoutError' ? 'timeout' : error.name === 'AbortError' || error.code === 'STALE_PRODUCT' ? 'interrupted' : details.includes(error.detail) ? error.detail : 'unavailable' : '';
      const response = transport || error?.transport;
      entry.owner.dataset.realmProductRefreshStage = ['primary','fallback','page'].includes(response?.stage) ? response.stage : '';
      entry.owner.dataset.realmProductRefreshResponse = response && ['json','html','other','absent'].includes(response.kind) ? String(response.status) + ':' + response.kind : '';
    }
    const contractSignature = product => {
      const contract = window.RealmAppIntegration?.getContract?.(product);
      return contract ? JSON.stringify([contract.raw_ordering_ready, contract.ordering_ready_type, contract.purchase_mode, contract.purchase_mode_source, contract.preorder_terms_state, contract.terms_reason, contract.current_batch, contract.dispatch, contract.raw_tags]) : null;
    };
    const refreshProduct = (entry, { signal, background = false } = {}) => {
      const integration = window.RealmAppIntegration, handle = entry.model.product.handle;
      if (!integration?.refreshProduct || !handle) throw new CommerceError('PRODUCT_MISMATCH', strings.configurationPending);
      if (signal?.aborted || products.get(entry.owner) !== entry || entry.owner.isConnected === false) throw new CommerceError('STALE_PRODUCT', strings.configurationPending);
      const generation = ++entry.refreshGeneration;
      entry.refreshController?.abort(); const controller = new window.AbortController(); entry.refreshController = controller;
      const relay = () => controller.abort(); signal?.addEventListener('abort', relay, { once: true });
      const timer = window.setTimeout(() => controller.abort(), 15000);
      const location = window.location.href, context = JSON.stringify(stableValue(config.contractContext)), choices = JSON.stringify(entry.model.options);
      const before = contractSignature(entry.model.product);
      entry.refreshPending = true; recordProductRefresh(entry, 'checking'); updateSelection(entry);
      let task;
      task = (async () => { try {
        const product = await integration.refreshProduct(handle, { productId: entry.model.product.id, variantId: productSelection(entry.model, false).variant?.id, signal: controller.signal, context: config.contractContext, fetch: window.fetch.bind(window), root: config.routes.root, location: window.location, DOMParser: window.DOMParser });
        if (controller.signal.aborted || generation !== entry.refreshGeneration || products.get(entry.owner) !== entry || entry.owner.isConnected === false || location !== window.location.href || context !== JSON.stringify(stableValue(config.contractContext)) || background && choices !== JSON.stringify(entry.model.options)) throw new CommerceError('STALE_PRODUCT', strings.configurationPending);
        entry.model.update(product);
        integration.attachContract(entry.model.product, integration.getContract(product), config.contractContext);
        entry.refreshError = false;
        recordProductRefresh(entry, 'ready', null, product.realm_contract_transport);
        const selection = updateSelection(entry);
        return { ...selection, contractChanged: before !== null && before !== contractSignature(entry.model.product) };
      } catch (error) {
        if (generation === entry.refreshGeneration && products.get(entry.owner) === entry && entry.owner.isConnected !== false) { integration.invalidate?.(entry.model.product); entry.refreshError = true; recordProductRefresh(entry, 'error', error); updateSelection(entry); }
        throw error;
      } finally { window.clearTimeout(timer); signal?.removeEventListener('abort', relay); if (entry.refreshTask === task) { entry.refreshTask = null; entry.backgroundRefresh = false; entry.refreshPending = false; if (products.get(entry.owner) === entry && entry.owner.isConnected !== false) updateSelection(entry); } } })();
      entry.refreshTask = task; entry.backgroundRefresh = background;
      return task;
    };
    const waitForProductRefresh = async (task, signal) => {
      if (!signal) return task;
      if (signal.aborted) throw new CommerceError('STALE_PRODUCT', strings.configurationPending);
      let relay;
      const aborted = new Promise((_, reject) => { relay = () => reject(new CommerceError('STALE_PRODUCT', strings.configurationPending)); signal.addEventListener('abort', relay, { once: true }); });
      try { return await Promise.race([task, aborted]); } finally { signal.removeEventListener('abort', relay); }
    };
    const productRuntime = {
      getProduct: owner => owner?.isConnected !== false ? products.get(owner)?.model.product || null : null,
      refreshProduct: async (owner, { signal, onlyIfStale = false } = {}) => {
        const entry = products.get(owner);
        if (!entry || owner.isConnected === false || signal?.aborted) throw new CommerceError('STALE_PRODUCT', strings.configurationPending);
        if (entry.refreshTask || !onlyIfStale || !window.RealmAppIntegration?.getContract?.(entry.model.product)) {
          await waitForProductRefresh(entry.refreshTask || refreshProduct(entry, { signal, background: true }), signal);
        }
        if (signal?.aborted || products.get(owner) !== entry || owner.isConnected === false || !window.RealmAppIntegration?.getContract?.(entry.model.product)) throw new CommerceError('STALE_PRODUCT', strings.configurationPending);
        return entry.model.product;
      }
    };
    const applyBusy = (value, ready) => {
      busy = value || checkoutInFlight || nativeHandoff;
      for (const control of document.querySelectorAll('[data-cart-change]')) if (!cartBaselineDisabled.has(control)) cartBaselineDisabled.set(control, control.disabled);
      const controls = document.querySelectorAll('[data-cart-change], [data-cart-retry], [data-product-retry], [data-realm-product] input, [data-realm-product] select, [data-realm-card-size-open], [data-add-to-cart], [data-quantity-step], [data-realm-checkout-form] input, [data-realm-checkout-form] select, [data-realm-checkout-form] textarea');
      for (const control of controls) {
        const successfulCartControl = nativeHandoff && control.closest?.('[data-realm-checkout-form]') && control.matches?.('input, select, textarea');
        if (busy && !successfulCartControl) { if (!disabled.has(control)) disabled.set(control, control.disabled); control.disabled = true; }
        else if (disabled.has(control)) { control.disabled = disabled.get(control); disabled.delete(control); }
      }
      for (const control of document.querySelectorAll('[data-cart-change]')) control.disabled = busy || !ready || cartBaselineDisabled.get(control);
      for (const entry of products.values()) updateSelection(entry);
      for (const node of document.querySelectorAll('[data-cart-content]')) node.setAttribute('aria-busy', String(busy));
      updateCheckout();
    };
    function updateCheckout() {
      const ready = checkoutReadiness(service, bundles, nativeHandoff), reason = checkoutInFlight ? 'checkoutChecking' : checkoutIssue || ready.reason || 'checkoutReady';
      const blockedIssue = checkoutIssue && checkoutIssue !== 'checkoutChanged' && checkoutIssue !== 'checkoutFailed';
      for (const form of document.querySelectorAll('[data-realm-checkout-form]')) {
        const button = form.querySelector('.realm-checkout[name="checkout"]');
        form.setAttribute('data-realm-checkout-state', nativeHandoff ? 'entering' : checkoutInFlight ? 'checking' : checkoutIssue === 'checkoutChanged' ? 'changed' : ready.ready && !blockedIssue && !button?.dataset.realmCheckoutServerReason ? 'ready' : 'blocked');
        form.setAttribute('aria-busy', String(checkoutInFlight || nativeHandoff));
        if (button) {
          button.disabled = Boolean(!nativeHandoff && (button.dataset.realmCheckoutServerReason || checkoutInFlight || !ready.ready || blockedIssue));
          button.setAttribute('aria-disabled', String(button.disabled || nativeHandoff));
          button.setAttribute('aria-busy', String(checkoutInFlight || nativeHandoff));
          if (!button.dataset.realmCheckoutLabel) button.dataset.realmCheckoutLabel = actionLabel(button).textContent;
          setActionLabel(button, nativeHandoff ? strings.checkoutEntering : checkoutInFlight ? strings.checkoutChecking : button.dataset.realmCheckoutLabel);
        }
        const message = form.querySelector('[data-realm-checkout-status]'); if (message) message.textContent = !button?.dataset.realmCheckoutServerReason && reason === 'checkoutReady' ? '' : strings[button?.dataset.realmCheckoutServerReason || reason] || strings.checkoutFailed;
      }
      if (!checkoutInFlight && !nativeHandoff && (checkoutIssue || ready.reason === 'checkoutReconcile')) for (const retry of document.querySelectorAll('[data-cart-retry]')) retry.hidden = false;
    }
    const service = new CartService({
      // Shopify installs its cart observer asynchronously. Resolve the current
      // fetch chain per request so genuine cart adds keep that observer.
      fetch: (...args) => window.fetch(...args), routes: localeRoutes(config.routes || (window.Shopify && window.Shopify.routes) || {}, window.location.origin), sectionsUrl: window.location.pathname + window.location.search,
      validateOperation: async op => {
        if (op.kind !== 'add') return;
        const entry = products.get(op.owner);
        if (!entry || entry.owner.isConnected === false || !entry.model.product.variants.some(v => String(v.id) === String(op.id))) throw new CommerceError('INVALID_VARIANT', strings.unavailable);
        const state = await refreshProduct(entry);
        const variant = entry.model.product.variants.find(v => String(v.id) === String(op.id));
        if (state.contractChanged) throw new CommerceError('PRODUCT_CHANGED', strings.productChanged, { reviewRequired: true });
        if (!state.ready || String(state.variant.id) !== String(op.id) || !variant || variant.available !== true) throw new CommerceError('INVALID_VARIANT', strings.unavailable);
        quantityAllowed(op.quantity, variant);
      },
      onSnapshot: (cart, html) => {
        bundles.reconcile(cart);
        for (const badge of document.querySelectorAll('[data-cart-count]')) {
          const count = merchandiseItemCount(cart, config.protection);
          badge.textContent = String(count);
          if (badge.classList.contains('realm-cart-count')) badge.hidden = count === 0;
        }
        if (html !== null) {
          cartDrawerUI.beforeRender();
          for (const content of document.querySelectorAll('[data-cart-content]')) {
            const active = document.activeElement;
            if (content.contains(active) && active.matches?.('[data-cart-change]')) pendingCartFocus = { content, controls: Array.from(content.querySelectorAll('[data-cart-change]')), target: active };
            const bodyScroll = content.querySelector('.realm-cart-shopping-body')?.scrollTop || 0;
            content.replaceChildren(...serverCartContent(document, html, content.closest('#RealmCartDrawer') ? 'drawer' : 'page'));
            const bodyAfterRender = content.querySelector('.realm-cart-shopping-body');
            if (bodyAfterRender) bodyAfterRender.scrollTop = bodyScroll;
          }
        }
        cartDrawerUI.snapshot(cart);
        document.dispatchEvent(new window.CustomEvent('realm:cart-confirmed', { detail: { cart, rendered: html !== null } }));
      },
      onState: state => {
        cartDrawerUI.state(state);
        if (state.phase === 'pending' && !checkoutInFlight && !nativeHandoff) checkoutIssue = null;
        if (state.phase === 'pending') {
          const active = document.activeElement, content = active?.closest?.('[data-cart-content]');
          if (content && active.matches?.('[data-cart-change]')) pendingCartFocus = { content, controls: Array.from(content.querySelectorAll('[data-cart-change]')), target: active };
        }
        if (state.phase === 'error') {
          const error = state.error || {};
          const code = String(error.code || error.name || 'UNKNOWN_ERROR').slice(0, 64);
          const detail = typeof error.message === 'string' ? error.message.slice(0, 500) : 'Cart refresh failed.';
          for (const node of document.querySelectorAll('[data-cart-status]')) { node.setAttribute('data-error-code', code); node.setAttribute('data-error-detail', detail); }
          if (window.console && typeof window.console.warn === 'function') window.console.warn('Realm cart operation failed:', code);
        } else if (state.phase === 'success' || state.phase === 'refreshed') {
          for (const node of document.querySelectorAll('[data-cart-status]')) { node.removeAttribute('data-error-code'); node.removeAttribute('data-error-detail'); }
        }
        applyBusy(state.busy, state.renderReady);
        if (!state.busy && pendingCartFocus) {
          const { content, controls, target } = pendingCartFocus; pendingCartFocus = null;
          const active = document.activeElement;
          if (content.isConnected !== false && (!active || active === document.body || active === document.documentElement || active === target || active.isConnected === false)) cartFocusTarget(content, controls, target)?.focus({ preventScroll: true });
        }
        for (const button of document.querySelectorAll('[data-cart-retry]')) { button.hidden = !state.recovery && !checkoutIssue && bundles.readiness().ready; setActionLabel(button, strings.retry); }
        for (const entry of products.values()) for (const button of entry.owner.querySelectorAll('[data-product-retry]')) {
          button.hidden = !entry.refreshError && !(state.recovery && state.recovery.operation && state.recovery.operation.kind === 'add' && entry.owner === state.recovery.operation.owner);
          setActionLabel(button, entry.pageRefreshRequired ? strings.refreshPage : strings.retry);
        }
        for (const entry of products.values()) {
          const pendingOperation = state.operation || state.recovery?.operation;
          const pendingAdd = Boolean(state.busy && state.phase === 'pending' && pendingOperation?.kind === 'add' && entry.owner === pendingOperation.owner);
          entry.form.querySelector('[data-add-to-cart]')?.setAttribute('aria-busy', String(pendingAdd));
          for (const retry of entry.owner.querySelectorAll('[data-product-retry]')) retry.setAttribute('aria-busy', String(pendingAdd));
        }
        if (state.phase === 'pending') {
          status(strings.updating);
          if (state.operation && state.operation.kind === 'add') {
            const entry = products.get(state.operation.owner);
            if (entry) status(strings.adding, entry.owner);
          }
        }
        if (state.phase === 'success') status(state.operation.kind === 'add' ? strings.added : state.operation.quantity === 0 ? strings.removed : strings.quantityUpdated);
        if (state.phase === 'refreshed') status(strings.refreshed);
        if (state.phase === 'review') status(state.error.message);
        if (state.phase === 'error') status(state.error.refreshOnly ? strings.displayError : state.error.message || strings.cartError);
      }
    });
    const protection = window.RealmProtectionGate?.mount(document, window, config.protection, service);
    async function validateCheckoutLines(cart) {
      const integration = window.RealmAppIntegration;
      if (!integration?.refreshProduct) throw new CommerceError('CHECKOUT_GUARD_UNAVAILABLE', strings.configurationPending, { checkoutReason: 'configurationPending' });
      const verified = new Map(), handles = new Map();
      for (const line of cart.items) {
        const handle = line.handle || (() => { try { return new URL(line.url, window.location.origin).pathname.match(/\/products\/([a-z0-9-]+)/i)?.[1]; } catch (_) { return null; } })();
        if (!handle || !/^[a-z0-9][a-z0-9-]*$/i.test(handle)) throw new CommerceError('CHECKOUT_LINE_PENDING', strings.configurationPending, { checkoutReason: 'configurationPending' });
        if (!handles.has(handle)) handles.set(handle, line);
      }
      const entries = Array.from(handles), failures = new Map();
      let next = 0;
      const worker = async () => {
        for (;;) {
          const index = next++;
          if (index >= entries.length) return;
          const [handle, line] = entries[index];
          try { verified.set(handle, await integration.refreshProduct(handle, { productId: line.product_id, variantId: line.variant_id || line.id, context: config.contractContext, fetch: window.fetch.bind(window), root: config.routes.root, location: window.location, DOMParser: window.DOMParser })); }
          catch (error) { const reason = ['configurationPending','availabilityPending','preorderPending','pricePending','contentPending','unavailable'].includes(error?.reason) ? error.reason : 'configurationPending'; failures.set(index, new CommerceError('CHECKOUT_LINE_PENDING', strings[reason] || strings.configurationPending, { checkoutReason: reason })); }
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, entries.length) }, worker));
      if (failures.size) throw failures.get(Math.min(...failures.keys()));
      for (const line of cart.items) {
        const handle = line.handle || (() => { try { return new URL(line.url, window.location.origin).pathname.match(/\/products\/([a-z0-9-]+)/i)?.[1]; } catch (_) { return null; } })();
        const product = verified.get(handle), variant = product?.variants?.find(value => String(value.id) === String(line.variant_id || line.id));
        if (String(product?.id) !== String(line.product_id) || !variant) throw new CommerceError('CHECKOUT_LINE_PENDING', strings.configurationPending, { checkoutReason: 'configurationPending' });
        const guard = integration.existingCartOrderingGuard ? integration.existingCartOrderingGuard(product, variant, cart, line) : integration.orderingGuard(product, variant);
        if (!guard.ready) throw new CommerceError('CHECKOUT_LINE_PENDING', strings[guard.reason] || strings.configurationPending, { checkoutReason: guard.reason || 'configurationPending' });
        quantityAllowed(line.quantity, variant);
      }
    }
    document.addEventListener('submit', async event => {
      const form = event.target.closest?.('[data-realm-checkout-form]');
      if (!form) return;
      if (approvedCheckout?.form === form && event.submitter === approvedCheckout.button && form.isConnected !== false && !approvedCheckout.button.disabled) {
        approvedCheckout = null; checkoutInFlight = false; nativeHandoff = true; applyBusy(false, service.renderReady); return;
      }
      event.preventDefault(); event.stopImmediatePropagation?.();
      if (checkoutInFlight || nativeHandoff) return;
      const initial = checkoutReadiness(service, bundles);
      if (!initial.ready || form.isConnected === false || form.querySelector('.realm-checkout')?.dataset.realmCheckoutServerReason) { updateCheckout(); return; }
      const surface = form.getAttribute('data-realm-checkout-surface');
      if (!['page', 'drawer'].includes(surface)) return;
      if (checkoutSuspended) return;
      const attempt = ++checkoutGeneration;
      const assertCheckoutActive = () => {
        if (checkoutSuspended || attempt !== checkoutGeneration) throw new CommerceError('CHECKOUT_CANCELLED', strings.checkoutFailed);
      };
      checkoutInFlight = true; checkoutIssue = null; applyBusy(false, service.renderReady);
      checkoutDiagnostics(null);
      let checkoutPhase = 'initial-cart';
      const markCheckoutPhase = phase => {
        checkoutPhase = phase;
        document.querySelector('[data-realm-checkout-form][data-realm-checkout-surface="' + surface + '"]')?.setAttribute?.('data-realm-checkout-guard-phase', phase);
      };
      markCheckoutPhase(checkoutPhase);
      try {
        if (config.protection?.enabled === true && !protection) throw new CommerceError('PROTECTION_GUARD_UNAVAILABLE', strings.protectionPending, { checkoutReason: 'protectionPending' });
        let reviewed = checkoutCartSignature(service.cart);
        const merchandiseReviewed = protection?.merchandiseSignature(service.cart);
        markCheckoutPhase('protection-reconcile');
        const protectionGeneration = protection ? await protection.reconcile({ assertActive: assertCheckoutActive }) : null;
        assertCheckoutActive();
        markCheckoutPhase('protection-merchandise');
        if (protection && merchandiseReviewed !== protection.merchandiseSignature(service.cart)) throw new CommerceError('CHECKOUT_CHANGED', strings.checkoutChanged, { checkoutReason: 'checkoutChanged' });
        if (protectionGeneration !== null) {
          // The shopper's protection choice may legitimately add or remove its
          // fee. Accept that reconciled change for either choice, then keep all
          // later cart snapshots pinned to the fully reviewed state.
          protection.assert(protectionGeneration, service.cart);
          reviewed = checkoutCartSignature(service.cart);
        }
        const protectionReviewed = protectionGeneration === null ? null : checkoutProtectionSignature(service.cart, config.protection);
        const assertReviewedCart = () => {
          protection?.assert(protectionGeneration, service.cart);
          const currentReview = checkoutCartSignature(service.cart);
          if (protectionGeneration === null) {
            if (reviewed !== currentReview) throw new CommerceError('CHECKOUT_CHANGED', strings.checkoutChanged, { checkoutReason: 'checkoutChanged' });
          } else {
            if (merchandiseReviewed !== protection.merchandiseSignature(service.cart) || protectionReviewed !== checkoutProtectionSignature(service.cart, config.protection)) throw new CommerceError('CHECKOUT_CHANGED', strings.checkoutChanged, { checkoutReason: 'checkoutChanged' });
            reviewed = currentReview;
          }
        };
        // Protection reconciliation already fetched and rendered a fresh cart.
        // Keep its exact fee/merchandise review instead of immediately repeating
        // that same cart + section read. The final render below remains required
        // because server ordering restrictions can change during stock checks.
        if (!protection) await service.refresh();
        assertCheckoutActive();
        markCheckoutPhase('refreshed-cart');
        assertReviewedCart();
        await validateCheckoutLines(service.cart);
        assertCheckoutActive();
        await service.refresh();
        assertCheckoutActive();
        markCheckoutPhase('verified-cart');
        assertReviewedCart();
        const current = checkoutReadiness(service, bundles);
        if (!current.ready) throw new CommerceError('CHECKOUT_NOT_READY', strings[current.reason], { checkoutReason: current.reason });
        const connected = document.querySelector('[data-realm-checkout-form][data-realm-checkout-surface="' + surface + '"]');
        const button = connected?.querySelector('.realm-checkout[name="checkout"]');
        const action = connected && new URL(connected.getAttribute('action'), window.location.origin);
        if (!connected || connected.isConnected === false || !button || button.isConnected === false || button.dataset.realmCheckoutServerReason || connected.getAttribute('method')?.toLowerCase() !== 'post' || action.origin !== window.location.origin || action.pathname !== service.routes.cart || action.search || action.hash || typeof connected.requestSubmit !== 'function') throw new CommerceError('CHECKOUT_FORM_UNAVAILABLE', strings.checkoutFailed);
        protection?.assert(protectionGeneration, service.cart);
        assertCheckoutActive();
        checkoutInFlight = false; applyBusy(false, service.renderReady);
        button.disabled = false; approvedCheckout = { form: connected, button };
        markCheckoutPhase('native-submit');
        connected.requestSubmit(button);
        if (approvedCheckout) { approvedCheckout = null; throw new CommerceError('CHECKOUT_FORM_UNAVAILABLE', strings.checkoutFailed); }
      } catch (error) {
        if (checkoutSuspended || attempt !== checkoutGeneration) return;
        approvedCheckout = null; checkoutInFlight = false; nativeHandoff = false; checkoutIssue = error.checkoutReason || 'checkoutFailed';
        applyBusy(false, service.renderReady);
        const connected = document.querySelector('[data-realm-checkout-form][data-realm-checkout-surface="' + surface + '"]');
        connected?.setAttribute?.('data-realm-checkout-guard-phase', checkoutPhase);
        checkoutDiagnostics(error, checkoutPhase);
        connected?.querySelector('.realm-checkout[name="checkout"]')?.focus({ preventScroll: true });
      }
    }, true);
    async function changeCart(target, value) {
      if (busy || !service.renderReady) return;
      const key = cartControlKey(target);
      const container = target.closest('[data-cart-content]') || document;
      const oldControls = Array.from(container.querySelectorAll('[data-cart-change]'));
      status(strings.updating);
      try {
        await service.mutate({ kind: 'change', key, quantity: value });
        const next = cartFocusTarget(container, oldControls, target);
        if (next) next.focus({ preventScroll: true });
      } catch (error) {
        status(error.message);
        if (service.renderReady) {
          const next = cartFocusTarget(container, oldControls, target);
          if (next) next.focus({ preventScroll: true });
        }
      }
    }
    let cardHoverOpener = null;
    function showCardSizes(card, open, { focus = true } = {}) {
      const panel = card && card.querySelector('[data-realm-card-sizes]');
      const trigger = card && card.querySelector('[data-realm-card-size-open]');
      if (!panel || !trigger) return;
      if (!open && cardHoverOpener?.card === card) cardHoverOpener = null;
      if (open && panel.dataset.open !== 'true') {
        if (busy) return;
        for (const other of document.querySelectorAll('[data-realm-card-sizes][data-open="true"]')) {
          if (other !== panel) showCardSizes(other.closest('[data-realm-card-product]'), false, { focus: false });
        }
        const entry = products.get(card);
        if (entry) {
          entry.model.options.fill(null);
          for (const input of panel.querySelectorAll('[data-option-index]')) input.checked = false;
          updateSelection(entry);
          productRuntime.refreshProduct(card, { onlyIfStale: true }).catch(() => {});
        }
      }
      panel.dataset.open = String(open); panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open)); trigger.setAttribute('aria-expanded', String(open));
      const next = open ? panel.querySelector('.realm-card-size-options input:not([disabled])') || panel.querySelector('button:not([disabled])') : trigger;
      if (focus && next) next.focus({ preventScroll: true });
    }
    const finePointer = () => Boolean(window.matchMedia?.('(hover: hover) and (pointer: fine)').matches);
    document.addEventListener('pointerover', event => {
      if (!finePointer() || event.pointerType === 'touch') return;
      const trigger = event.target.closest?.('[data-realm-card-size-open]');
      if (!trigger || trigger.contains(event.relatedTarget)) return;
      const card = trigger.closest('[data-realm-card-product]'), panel = card?.querySelector('[data-realm-card-sizes]');
      const newlyOpened = panel?.dataset.open !== 'true', rect = trigger.getBoundingClientRect();
      showCardSizes(card, true, { focus: false });
      if (newlyOpened && panel?.dataset.open === 'true' && Number.isFinite(event.clientX) && Number.isFinite(event.clientY)) cardHoverOpener = { card, rect, x: event.clientX, y: event.clientY };
    });
    const movedFromHoverOpener = event => cardHoverOpener && Number.isFinite(event.clientX) && Number.isFinite(event.clientY) && Math.hypot(event.clientX - cardHoverOpener.x, event.clientY - cardHoverOpener.y) > 3;
    document.addEventListener('pointermove', event => { if (event.pointerType === 'touch' || movedFromHoverOpener(event)) cardHoverOpener = null; }, { passive: true });
    document.addEventListener('pointerdown', event => { if (event.pointerType === 'touch' || movedFromHoverOpener(event)) cardHoverOpener = null; }, true);
    document.addEventListener('click', event => {
      const opener = cardHoverOpener;
      if (!opener) return;
      cardHoverOpener = null;
      if (event.detail === 0 || event.pointerType === 'touch' || opener.card.isConnected === false || !opener.card.contains(event.target)) return;
      const { clientX: x, clientY: y } = event, { rect } = opener;
      if (!Number.isFinite(x) || !Number.isFinite(y) || Math.hypot(x - opener.x, y - opener.y) > 3 || x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) return;
      // Hover can reveal a radio exactly beneath the opener. Cancel that same
      // pointer click before native change rather than treating it as a size.
      event.preventDefault(); event.stopImmediatePropagation();
      const radio = event.target.closest?.('[data-option-index]');
      if (radio?.type === 'radio') radio.checked = false;
    }, true);
    document.addEventListener('pointerout', event => {
      if (!finePointer() || event.pointerType === 'touch') return;
      const card = event.target.closest?.('[data-realm-card-product]'), media = card?.querySelector('.realm-card-media');
      if (!media || !media.contains(event.target) || media.contains(event.relatedTarget)) return;
      const panel = card.querySelector('[data-realm-card-sizes]');
      if (!panel || panel.dataset.open !== 'true' || panel.contains(document.activeElement) || busy) return;
      showCardSizes(card, false, { focus: false });
    });
    document.addEventListener('keydown', event => {
      cardHoverOpener = null;
      if (event.key !== 'Escape') return;
      const card = event.target.closest('[data-realm-card-product]');
      if (!card || !card.querySelector('[data-realm-card-sizes][data-open="true"]')) return;
      event.preventDefault(); event.stopPropagation(); showCardSizes(card, false);
    });
    // Keep the native form's variant current before Shopify's form-level
    // analytics listener sees the same deliberate option change.
    document.addEventListener('change', event => {
      const control = event.target.closest('[data-option-index]');
      if (!control || busy || ((control.type === 'radio' || control.type === 'checkbox') && !control.checked)) return;
      const entry = products.get(control.closest('[data-realm-product]'));
      if (!entry) return;
      try {
        if (entry.backgroundRefresh) { entry.refreshGeneration++; entry.refreshController?.abort(); }
        entry.model.choose(Number(control.dataset.optionIndex), control.value);
        const selection = updateSelection(entry);
        saveProductIntent(entry, window);
        // A deliberate new size can outlive its original server contract.
        // Refresh it promptly, but require the customer's Add after recovery.
        if (entry.recoveryAllowed && !window.RealmAppIntegration?.getContract?.(entry.model.product)) {
          refreshProduct(entry, { background: true }).catch(() => {});
        }
        if (control.closest('[data-realm-card-product]') && selection.ready) {
          const submitter = entry.form.querySelector('[data-add-to-cart]');
          if (submitter && !submitter.disabled) addProduct(entry);
        }
      }
      catch (error) { status(error.message, entry.owner); }
    }, true);
    document.addEventListener('change', event => {
      if (event.target.matches('input[data-cart-change], select[data-cart-change]')) { changeCart(event.target, event.target.value); return; }
      if (event.target.matches('[data-realm-product] [name="quantity"]')) { const entry = products.get(event.target.closest('[data-realm-product]')); if (entry) { updateQuantityControls(entry, entry.model.resolve().variant); saveProductIntent(entry, window); } }
    });
    async function addProduct(entry) {
      if (busy) return;
      if (!entry || entry.owner.isConnected === false || products.get(entry.owner) !== entry) return;
      const form = entry.form;
      try {
        const selection = updateSelection(entry);
        if (!selection.ready) return;
        const quantity = quantityAllowed((form.querySelector('[name="quantity"]') || { value: 1 }).value, selection.variant);
        const caller = entry.owner.querySelector('[data-realm-card-size-open]') || form.querySelector('[data-add-to-cart]');
        let fitProperties = {};
        try { fitProperties = window.RealmFitQuiz?.propertiesFor?.(form, entry.model.product.id) || {}; } catch (_) {}
        const properties = productFormProperties(form, fitProperties);
        status(strings.adding, entry.owner);
        await service.mutate({ kind: 'add', id: selection.variant.id, quantity, properties, owner: entry.owner });
        status(strings.added, entry.owner); dialogs.open(document.getElementById('RealmCartDrawer'), caller);
      } catch (error) { status(entry.pageRefreshRequired ? strings.contextChanged : error.message, entry.owner); }
    }
    document.addEventListener('submit', event => {
      const form = event.target.closest('form[data-product-form]');
      if (!form) return;
      event.preventDefault();
      addProduct(products.get(form.closest('[data-realm-product]')));
    }, true);
    document.addEventListener('click', async event => {
      const target = event.target.closest('button, a, [data-cart-open], [data-dialog-open]');
      if (!target) return;
      if (target.matches('[data-price-select-option]')) {
        event.preventDefault(); if (busy) return;
        const entry = products.get(target.closest('[data-realm-product]')); if (entry) focusMissingProductOption(entry, window); return;
      }
      if (target.matches('.realm-checkout[disabled]')) { event.preventDefault(); event.stopImmediatePropagation?.(); return; }
      if (target.matches('[data-realm-card-size-open], [data-realm-card-size-close]')) {
        event.preventDefault(); showCardSizes(target.closest('[data-realm-card-product]'), target.matches('[data-realm-card-size-open]'), { focus: event.detail === undefined || event.detail === 0 || target.matches('[data-realm-card-size-close]') }); return;
      }
      if (target.closest('#RealmMenu') || target.dataset.dialogOpen === 'RealmMenu') return;
      if (target.matches('[data-dialog-close]')) { event.preventDefault(); dialogs.close(target.closest('dialog')); return; }
      if (target.matches('[data-cart-open]')) {
        event.preventDefault(); dialogs.open(document.getElementById('RealmCartDrawer'), target);
        if (!busy) service.refresh().catch(() => {}); return;
      }
      if (target.matches('[data-dialog-open]')) { event.preventDefault(); dialogs.open(document.getElementById(target.dataset.dialogOpen), target); return; }
      if (target.matches('[data-cart-retry], [data-product-retry]')) {
        event.preventDefault(); if (busy) return;
        let retryAttempt = null, retryPhase = 'cart-recover';
        try {
          if (target.matches('[data-product-retry]')) { const entry = products.get(target.closest('[data-realm-product]')); if (entry?.refreshError) {
            if (entry.pageRefreshRequired) {
              try { if (saveProductIntent(entry, window)) { const state = window.history.state; window.history.replaceState({ ...state, realmProductContextRefresh: { productId: String(entry.model.product.id), path: window.location.pathname, variantParameter: productVariantParameter(window.location.search) } }, ''); } } catch (_) {}
              window.location.reload(); return;
            }
            await refreshProduct(entry); status(strings.productChanged, entry.owner); return;
          } }
          if (target.matches('[data-cart-retry]')) {
            if (checkoutSuspended) return;
            retryAttempt = ++checkoutGeneration;
            const assertRetryActive = () => {
              if (checkoutSuspended || retryAttempt !== checkoutGeneration) throw new CommerceError('CHECKOUT_CANCELLED', strings.checkoutFailed);
            };
            checkoutInFlight = true; applyBusy(false, service.renderReady);
            const hadRecovery = Boolean(service.recovery);
            const result = await service.retry({ assertActive: assertRetryActive });
            assertRetryActive();
            // Only this explicit shopper action may recover the protection
            // gate. Resolve any ambiguous cart operation first, then require
            // the provider's current choice and exact fee to agree afresh.
            if (protection) {
              retryPhase = 'protection-recover';
              checkoutDiagnostics(null, 'protection-recover');
              await (typeof protection.recover === 'function' ? protection.recover({ assertActive: assertRetryActive }) : protection.reconcile({ assertActive: assertRetryActive }));
            } else if (!hadRecovery) await service.refresh();
            assertRetryActive();
            bundles.reviewRestored(service.cart);
            checkoutIssue = result.reviewRequired ? 'checkoutChanged' : null;
            checkoutDiagnostics(null);
            updateCheckout();
            if (result.confirmed && result.operation?.kind === 'add') {
              const entry = products.get(result.operation.owner);
              if (entry) status(strings.added, entry.owner);
              dialogs.open(document.getElementById('RealmCartDrawer'), entry ? entry.owner.querySelector('[data-realm-card-size-open]') || entry.form.querySelector('[data-add-to-cart]') : target);
            }
            return;
          }
          const result = await service.retry();
          if (result.confirmed && result.operation && result.operation.kind === 'add') {
            const entry = products.get(result.operation.owner);
            if (entry) status(strings.added, entry.owner);
            dialogs.open(document.getElementById('RealmCartDrawer'), entry ? entry.owner.querySelector('[data-realm-card-size-open]') || entry.form.querySelector('[data-add-to-cart]') : target);
          }
        } catch (error) {
          if (retryAttempt !== null && (checkoutSuspended || retryAttempt !== checkoutGeneration)) return;
          const owner = target.closest('[data-realm-product]');
          if (owner) status(products.get(owner)?.pageRefreshRequired ? strings.contextChanged : error.message, owner);
          else {
            checkoutIssue = error.checkoutReason || 'checkoutRecovery';
            checkoutDiagnostics(error, retryPhase);
            updateCheckout(); status(strings[checkoutIssue] || strings.checkoutFailed);
          }
        } finally {
          if (retryAttempt !== null && !checkoutSuspended && retryAttempt === checkoutGeneration) { checkoutInFlight = false; applyBusy(false, service.renderReady); }
        }
        return;
      }
      if (target.matches('[data-cart-change]')) {
        event.preventDefault(); if (busy || !service.renderReady) return;
        const value = target.dataset.quantity !== undefined ? target.dataset.quantity : target.dataset.key || target.dataset.cartKey ? target.dataset.cartChange : target.getAttribute('data-cart-quantity');
        await changeCart(target, value);
        return;
      }
      if (target.matches('[data-quantity-step]')) {
        event.preventDefault(); if (busy) return;
        const entry = products.get(target.closest('[data-realm-product]')); const input = entry && entry.form.querySelector('[name="quantity"]');
        if (input) { try { input.value = String(steppedQuantity(input.value, Number(target.dataset.quantityStep), entry.model.resolve().variant || {})); updateQuantityControls(entry, entry.model.resolve().variant); saveProductIntent(entry, window); } catch (error) { status(error.message, entry.owner); } }
        return;
      }
      if (target.matches('[data-gallery-index], [data-gallery-step]')) {
        event.preventDefault();
        const owner = target.closest('[data-realm-product]'); const entry = products.get(owner); const main = owner && owner.querySelector('[data-gallery-main]');
        if (!entry || !main) return;
        const custom = owner.querySelector('[data-gallery-json]'); let images;
        try { images = custom ? JSON.parse(custom.textContent) : entry.model.product.images; } catch (_) { return; }
        if (!Array.isArray(images) || !images.length) return;
        let gallery = galleries.get(main); if (!gallery) { gallery = new GalleryController(main, window); galleries.set(main, gallery); }
        let index;
        try { index = target.matches('[data-gallery-step]') ? galleryIndex(Number(gallery.requested?.index ?? owner.dataset.galleryActiveIndex ?? 0), Number(target.dataset.galleryStep), images.length) : integer(target.dataset.galleryIndex, 0); } catch (_) { return; }
        const image = images[index]; const src = typeof image === 'string' ? image : image && (image.src || image.url);
        if (!src) return;
        const url = new URL(src, window.location.origin); if (!['https:', 'http:'].includes(url.protocol)) return;
        gallery.show(url.href, typeof image === 'object' && image.alt ? image.alt : entry.model.product.title, { ...(typeof image === 'object' ? image : {}), gallery_index: index, gallery_count: images.length });
        return;
      }
      if (target.tagName === 'A' && dialogs.active && !target.hasAttribute('download') && (!target.target || target.target === '_self')) {
        const url = new URL(target.href, window.location.origin);
        if (url.pathname !== window.location.pathname || url.search !== window.location.search || url.origin !== window.location.origin) dialogs.abandon();
      }
    });
    document.addEventListener('realm:collection-before-update', event => {
      if (dialogs.context) event.detail.scrollY = dialogs.context.y;
      dialogs.closeImmediate(dialogs.active, true);
    });
    document.addEventListener('realm:collection-updated', event => {
      const scope = event.detail.scope; mountProducts(scope); dialogs.register(scope);
      service.sectionsUrl = window.location.pathname + window.location.search;
    });
    document.addEventListener('realm:product-discovery-updated', event => {
      const scope = event.detail.scope; mountProducts(scope); dialogs.register(scope);
    });
    document.addEventListener('realm:search-results-updated', event => {
      const scope = event.detail.scope; mountProducts(scope); dialogs.register(scope);
    });
    document.addEventListener('shopify:section:load', event => {
      mountProducts(event.target); dialogs.register(event.target);
    });
    document.addEventListener('shopify:section:unload', event => {
      const scope = event.target, owns = node => node && (scope === node || scope?.contains?.(node));
      if (owns(dialogs.active) || owns(dialogs.suspendedGuide?.dialog) || owns(dialogs.context?.caller)) dialogs.closeImmediate(dialogs.active || dialogs.suspendedGuide?.dialog, true);
      unregisterProducts(products, scope, disposeProduct);
    });
    window.addEventListener('pagehide', () => { checkoutSuspended = true; checkoutGeneration++; approvedCheckout = null; for (const entry of products.values()) { saveProductIntent(entry, window); entry.refreshGeneration++; entry.refreshController?.abort(); } });
    const restore = event => { if (event?.type === 'pageshow') { const returning = checkoutSuspended; checkoutSuspended = false; if (event.persisted || nativeHandoff || returning) { checkoutGeneration++; approvedCheckout = null; checkoutInFlight = false; nativeHandoff = false; checkoutIssue = null; applyBusy(false, service.renderReady); } } if (!busy && document.visibilityState !== 'hidden') { service.refresh().catch(() => {}); for (const entry of products.values()) if (entry.owner.querySelector('[data-pdp-price]') && entry.recoveryAllowed && !window.RealmAppIntegration?.getContract?.(entry.model.product)) productRuntime.refreshProduct(entry.owner, { onlyIfStale: true }).catch(() => {}); } };
    window.addEventListener('pageshow', restore); window.addEventListener('focus', restore);
    document.addEventListener('visibilitychange', restore);
    window.RealmAppBridge = {
      suspendGuide: (dialog, caller) => dialogs.suspendGuide(dialog, caller),
      resumeGuide: token => dialogs.resumeGuide(token),
      interruptGuide: reason => { const interrupted = dialogs.interruptGuide(reason); if (interrupted && !dialogs.active) dialogs.finish(true); return interrupted; },
      setBundleCheckoutState: (id, state) => bundles.set(id, state),
      checkoutReadiness: () => checkoutReadiness(service, bundles, checkoutInFlight || nativeHandoff),
      checkoutInProgress: () => checkoutInFlight || nativeHandoff,
      refreshCart: () => service.refresh(),
      openCart: caller => { dialogs.open(document.getElementById('RealmCartDrawer'), caller); return service.refresh(); }
    };
    if (window.RealmCommerce) window.RealmCommerce.runtime = productRuntime;
    restore();
    return { service, dialogs, products, mountProducts, bundles, runtime: productRuntime, checkoutReadiness: () => checkoutReadiness(service, bundles, checkoutInFlight || nativeHandoff) };
  }

  return { CommerceError, CartService, VariantSelection, DialogController, GalleryController, actionLabel, setActionLabel, registerProducts, unregisterProducts, integer, lineProperties, productFormProperties, quantityAllowed, quantityBounds, steppedQuantity, galleryIndex, productSelection, productInventoryState, renderProductInventory, formatVariantPrice, variantPriceState, filteredCardVariant, productPriceState, renderPriceView, mainProductPriceState, productOwnedVariant, applyProductOptions, readProductIntent, seedMainProduct, saveProductIntent, focusMissingProductOption, localeRoutes, validCart, merchandiseItemCount, cartFingerprint, checkoutCartSignature, checkoutProtectionSignature, currentBundleAttempt, reconcileBundleCheckout, bundleAttemptSignature, matchesBundleCompletion, BundleCheckoutRegistry, checkoutReadiness, sectionReadUrl, serverCartContent, alignCardPriceRows, createCardPriceRows, shippingProgress, shippingSnapshotMatches, cartRailPosition, CartRailController, createCartDrawerUI, mount };
});


